// src/components/ClientDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage, auth } from '../firebase';
import {
    collection,
    query,
    where,
    onSnapshot,
    orderBy,
    addDoc,
    updateDoc,
    deleteDoc,
    arrayUnion,
    serverTimestamp,
    writeBatch,
    doc,
    or
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged, signOut } from 'firebase/auth';

import SignatureModal from './SignatureModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const PROVINCES = [
    'ON', 'BC', 'AB', 'QC', 'MB', 'SK', 'NS', 'NB', 'NL', 'PE', 'NT', 'YT', 'NU'
];

const MARITAL_STATUSES = [
    'Single',
    'Married',
    'Common-Law',
    'Separated',
    'Divorced',
    'Widowed'
];

const emptySpouse = {
    firstName: '',
    lastName: '',
    dob: '',
    sin: '',
    ssn: '',
    email: '',
    phone1: '',
    phone2: '',
    street: '',
    city: '',
    province: 'ON',
    postal: '',
    country: 'Canada',
    isSelfEmployed: 'No',
    gstNumber: '',
    gstQuickMethod: 'No',
    hasRentalProperty: 'No'
};

const ClientDashboard = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loadingWorkspace, setLoadingWorkspace] = useState(true);
    const [workspaceError, setWorkspaceError] = useState('');

    const [accessibleWorkspaces, setAccessibleWorkspaces] = useState([]);
    const [clientData, setClientData] = useState(null);
    const [firmTemplate, setFirmTemplate] = useState(null);
    const [preparerAvailabilityMap, setPreparerAvailabilityMap] = useState({});
    const [externalBusySlots, setExternalBusySlots] = useState([]);

    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [uploading, setUploading] = useState(false);
    const [savingPdfId, setSavingPdfId] = useState(null);
    const [selectedTag, setSelectedTag] = useState('General Docs');

    const [signatureRequests, setSignatureRequests] = useState([]);
    const [clientTasks, setClientTasks] = useState([]);
    const [activeSignatureModal, setActiveSignatureModal] = useState(null);
    const [viewingInvoice, setViewingInvoice] = useState(null);

    const [activeTab, setActiveTab] = useState('Home');
    const [selectedFolderYear, setSelectedFolderYear] = useState(null);
    const [showFirmInfo, setShowFirmInfo] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // --- CLIENT CALL / APPOINTMENT BOOKING STATE (SYNCED WITH CRM CALENDAR) ---
    const [allCalendarAppointments, setAllCalendarAppointments] = useState([]);
    const [bookingDate, setBookingDate] = useState(() => {
        const defaultDate = new Date();
        defaultDate.setDate(defaultDate.getDate() + 2);
        return defaultDate.toISOString().split('T')[0];
    });
    const [bookingTimeSlot, setBookingTimeSlot] = useState('10:00');
    const [bookingType, setBookingType] = useState('Phone Call');
    const [bookingTopic, setBookingTopic] = useState('');
    const [submittingBooking, setSubmittingBooking] = useState(false);

    useEffect(() => {
        if (!currentUser) return;
        const apptsQuery = query(collection(db, 'appointments'));
        const unsubAppts = onSnapshot(
            apptsQuery,
            (snap) => {
                setAllCalendarAppointments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            },
            (err) => {
                console.warn('Appointments listener warning:', err.message);
            }
        );
        return () => unsubAppts();
    }, [currentUser]);

    // --- INDIVIDUAL CLIENT SELF-ONBOARDING INTAKE STATE ---
    const [showOnboardingModal, setShowOnboardingModal] = useState(false);
    const [onboardingStep, setOnboardingStep] = useState(1);
    const [submittingOnboarding, setSubmittingOnboarding] = useState(false);
    const [sameAddressForSpouse, setSameAddressForSpouse] = useState(true);
    const [intakeForm, setIntakeForm] = useState({
        firstName: '',
        lastName: '',
        dob: '',
        sin: '',
        ssn: '',
        email: '',
        phone1: '',
        phone2: '',
        street: '',
        city: '',
        province: 'ON',
        postal: '',
        country: 'Canada',
        arrivalDate: '',
        departureDate: '',
        maritalStatus: 'Single',
        spouse: { ...emptySpouse },
        dependents: [],
        isSelfEmployed: 'No',
        gstNumber: '',
        gstQuickMethod: 'No',
        hasRentalProperty: 'No'
    });

    const messagesEndRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setCurrentUser(user);
            } else {
                navigate('/portal-login');
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    // Real-time listener for firm branding, preparer availability hours, and external calendar busy blocks
    useEffect(() => {
        if (!currentUser) return;
        const unsubConfig = onSnapshot(
            doc(db, 'config', 'mainSettings'),
            (configSnap) => {
                if (configSnap.exists()) {
                    const data = configSnap.data();
                    setFirmTemplate({
                        ...(data.invoiceSettings || {}),
                        ...(data.globalInvoiceTemplate || {}),
                        preparerTemplates: data.preparerTemplates || {},
                        appName: data.appName || 'NVS'
                    });
                    setPreparerAvailabilityMap(data.preparerAvailability || {});
                    setExternalBusySlots(Array.isArray(data.externalBusySlots) ? data.externalBusySlots : []);
                }
            },
            (e) => {
                console.warn("Could not load firm template settings:", e);
            }
        );
        return () => unsubConfig();
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser?.email) return;
        setLoadingWorkspace(true);
        setWorkspaceError('');

        const rawEmail = currentUser.email.trim();
        const lowerEmail = rawEmail.toLowerCase();

        const clientQuery = query(
            collection(db, 'clients'),
            or(
                where('email', '==', rawEmail),
                where('email', '==', lowerEmail),
                where('corpEmail', '==', rawEmail),
                where('corpEmail', '==', lowerEmail),
                where('authorizedEmails', 'array-contains', rawEmail),
                where('authorizedEmails', 'array-contains', lowerEmail)
            )
        );

        const unsubscribeClient = onSnapshot(
            clientQuery,
            (snapshot) => {
                if (!snapshot.empty) {
                    const workspaces = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                    setAccessibleWorkspaces(workspaces);

                    setClientData(prev => {
                        if (prev && workspaces.find(w => w.id === prev.id)) {
                            return workspaces.find(w => w.id === prev.id);
                        }
                        const personalWorkspace = workspaces.find(
                            w => (w.email || w.corpEmail || '').toLowerCase() === lowerEmail
                        );
                        return personalWorkspace || workspaces[0];
                    });
                } else {
                    setAccessibleWorkspaces([]);
                    setClientData(null);
                }
                setLoadingWorkspace(false);
            },
            (err) => {
                console.error("Client workspace listener error:", err);
                setWorkspaceError(err.message || 'Permission denied loading client workspace.');
                setLoadingWorkspace(false);
            }
        );
        return () => unsubscribeClient();
    }, [currentUser]);

    // Check if the active Individual workspace requires self-onboarding intake
    useEffect(() => {
        if (!clientData) return;

        const isIndividual = !clientData.clientType || clientData.clientType === 'Individual';
        const needsIntake = isIndividual && clientData.requiresOnboarding === true && !clientData.onboardingCompleted;

        if (needsIntake) {
            const nameParts = (clientData.name || '').trim().split(/\s+/);
            const fallbackFirst = nameParts[0] || '';
            const fallbackLast = nameParts.slice(1).join(' ') || '';

            setIntakeForm({
                firstName: clientData.firstName || fallbackFirst,
                lastName: clientData.lastName || fallbackLast,
                dob: clientData.dob || '',
                sin: clientData.sin || '',
                ssn: clientData.ssn || '',
                email: clientData.email || currentUser?.email || '',
                phone1: clientData.phone1 || '',
                phone2: clientData.phone2 || '',
                street: clientData.street || '',
                city: clientData.city || '',
                province: clientData.province || 'ON',
                postal: clientData.postal || '',
                country: clientData.country || 'Canada',
                arrivalDate: clientData.arrivalDate || '',
                departureDate: clientData.departureDate || '',
                maritalStatus: clientData.maritalStatus || 'Single',
                spouse: {
                    ...emptySpouse,
                    ...(clientData.spouse || {})
                },
                dependents: Array.isArray(clientData.dependents) ? clientData.dependents : [],
                isSelfEmployed: clientData.isSelfEmployed || 'No',
                gstNumber: clientData.gstNumber || '',
                gstQuickMethod: clientData.gstQuickMethod || 'No',
                hasRentalProperty: clientData.hasRentalProperty || 'No'
            });
            setOnboardingStep(1);
            setShowOnboardingModal(true);
        } else {
            setShowOnboardingModal(false);
        }
    }, [clientData, currentUser]);

    useEffect(() => {
        if (!clientData?.id) return;

        const messagesQuery = query(collection(db, 'clients', clientData.id, 'messages'), orderBy('timestamp', 'asc'));
        const unsubscribeMessages = onSnapshot(
            messagesQuery,
            (msgSnapshot) => {
                setMessages(msgSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
                scrollToBottom();
            },
            (err) => console.warn('Messages listener warning:', err.message)
        );

        const sigQuery = collection(db, 'clients', clientData.id, 'signatureRequests');
        const unsubscribeSigs = onSnapshot(
            sigQuery,
            (sigSnapshot) => {
                const allRequests = sigSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                const activeRequests = allRequests.filter(req =>
                    req.status !== 'canceled' && req.status !== 'cancelled'
                );
                setSignatureRequests(activeRequests);
            },
            (err) => console.warn('Signatures listener warning:', err.message)
        );

        const tasksQuery = collection(db, 'clients', clientData.id, 'clientTasks');
        const unsubscribeTasks = onSnapshot(
            tasksQuery,
            (taskSnapshot) => {
                setClientTasks(taskSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            },
            (err) => console.warn('Tasks listener warning:', err.message)
        );

        return () => { unsubscribeMessages(); unsubscribeSigs(); unsubscribeTasks(); };
    }, [clientData?.id]);

    useEffect(() => {
        if (activeTab === 'UnreadFiles' && clientData?.id && messages.length > 0) {
            const unreadFirmMsgs = messages.filter(m => m.sender === 'firm' && !m.readByClient);
            if (unreadFirmMsgs.length > 0) {
                const batch = writeBatch(db);
                unreadFirmMsgs.forEach(msg => {
                    const msgRef = doc(db, 'clients', clientData.id, 'messages', msg.id);
                    batch.update(msgRef, { readByClient: true });
                });
                batch.commit().catch(err => console.error("Error updating read receipts:", err));
            }
        }
    }, [activeTab, messages, clientData?.id]);

    const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    const handleLogout = async () => { await signOut(auth); navigate('/portal-login'); };

    const handleWorkspaceChange = (e) => {
        const selectedId = e.target.value;
        const selectedWorkspace = accessibleWorkspaces.find(w => w.id === selectedId);
        if (selectedWorkspace) {
            setClientData(selectedWorkspace);
            setActiveTab('Home');
            setSelectedFolderYear(null);
        }
    };

    // --- SUBMIT INDIVIDUAL ONBOARDING INTAKE FORM ---
    const handleIntakeStepNext = () => {
        if (onboardingStep === 1) {
            if (!intakeForm.firstName.trim() || !intakeForm.lastName.trim()) {
                alert('Please enter your First Name and Last Name.');
                return;
            }
            if (!intakeForm.dob) {
                alert('Please enter your Date of Birth.');
                return;
            }
            if (!intakeForm.sin.trim()) {
                alert('Please enter your Social Insurance Number (SIN).');
                return;
            }
            if (!intakeForm.street.trim() || !intakeForm.city.trim() || !intakeForm.postal.trim()) {
                alert('Please complete your street address, city, and postal code.');
                return;
            }
        }
        if (onboardingStep === 2) {
            const hasSpouse = intakeForm.maritalStatus === 'Married' || intakeForm.maritalStatus === 'Common-Law';
            if (hasSpouse && (!intakeForm.spouse.firstName.trim() || !intakeForm.spouse.lastName.trim())) {
                alert('Please provide your spouse/partner’s First and Last Name.');
                return;
            }
        }
        setOnboardingStep(prev => Math.min(3, prev + 1));
    };

    const handleAddDependent = () => {
        setIntakeForm(prev => ({
            ...prev,
            dependents: [
                ...prev.dependents,
                { firstName: '', lastName: prev.lastName || '', dob: '', sin: '', ssn: '' }
            ]
        }));
    };

    const handleDependentChange = (idx, field, value) => {
        setIntakeForm(prev => {
            const updated = [...prev.dependents];
            updated[idx] = { ...updated[idx], [field]: value };
            return { ...prev, dependents: updated };
        });
    };

    const handleRemoveDependent = (idx) => {
        setIntakeForm(prev => ({
            ...prev,
            dependents: prev.dependents.filter((_, i) => i !== idx)
        }));
    };

    const handleCompleteOnboarding = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!clientData?.id) return;

        setSubmittingOnboarding(true);
        try {
            const fullName = `${intakeForm.firstName.trim()} ${intakeForm.lastName.trim()}`.trim();
            const hasSpouse = intakeForm.maritalStatus === 'Married' || intakeForm.maritalStatus === 'Common-Law';

            const finalSpouse = hasSpouse
                ? {
                    ...intakeForm.spouse,
                    street: sameAddressForSpouse ? intakeForm.street.trim() : (intakeForm.spouse.street || '').trim(),
                    city: sameAddressForSpouse ? intakeForm.city.trim() : (intakeForm.spouse.city || '').trim(),
                    province: sameAddressForSpouse ? intakeForm.province : (intakeForm.spouse.province || 'ON'),
                    postal: sameAddressForSpouse ? intakeForm.postal.trim().toUpperCase() : (intakeForm.spouse.postal || '').trim().toUpperCase(),
                    country: sameAddressForSpouse ? intakeForm.country.trim() : (intakeForm.spouse.country || 'Canada')
                }
                : {};

            const clientDocRef = doc(db, 'clients', clientData.id);
            await updateDoc(clientDocRef, {
                firstName: intakeForm.firstName.trim(),
                lastName: intakeForm.lastName.trim(),
                name: fullName,
                dob: intakeForm.dob,
                sin: intakeForm.sin.trim(),
                ssn: intakeForm.ssn.trim(),
                email: (intakeForm.email || clientData.email || '').trim().toLowerCase(),
                phone1: intakeForm.phone1.trim(),
                phone2: intakeForm.phone2.trim(),
                street: intakeForm.street.trim(),
                city: intakeForm.city.trim(),
                province: intakeForm.province,
                postal: intakeForm.postal.trim().toUpperCase(),
                country: intakeForm.country.trim() || 'Canada',
                arrivalDate: intakeForm.arrivalDate || '',
                departureDate: intakeForm.departureDate || '',
                maritalStatus: intakeForm.maritalStatus,
                spouse: finalSpouse,
                dependents: intakeForm.dependents,
                isSelfEmployed: intakeForm.isSelfEmployed,
                gstNumber: intakeForm.isSelfEmployed === 'Yes' ? intakeForm.gstNumber.trim() : '',
                gstQuickMethod: intakeForm.isSelfEmployed === 'Yes' ? intakeForm.gstQuickMethod : 'No',
                hasRentalProperty: intakeForm.hasRentalProperty,
                requiresOnboarding: false,
                onboardingCompleted: true,
                onboardingCompletedAt: new Date().toISOString(),
                history: arrayUnion({
                    user: 'Client Portal',
                    timestamp: new Date().toLocaleString(),
                    action: 'Client completed self-onboarding tax intake form via portal.'
                })
            });

            try {
                await addDoc(collection(db, 'firmNotifications'), {
                    type: 'intake',
                    title: 'Client Completed Onboarding Intake',
                    message: `${fullName} completed their personal tax intake form on the portal.`,
                    clientId: clientData.id,
                    clientName: fullName,
                    isRead: false,
                    createdAt: serverTimestamp()
                });
            } catch (notifErr) {
                console.warn('Notification log skipped:', notifErr.message);
            }

            setShowOnboardingModal(false);
        } catch (err) {
            console.error('Error saving onboarding profile:', err);
            alert('Failed to save your onboarding information: ' + err.message);
        } finally {
            setSubmittingOnboarding(false);
        }
    };

    const getSignatureDocUrl = (req) => {
        if (!req) return '';
        return (
            req.finalDocumentUrl ||
            req.uploadedFileUrl ||
            req.originalFileUrl ||
            req.documentUrl ||
            req.fileUrl ||
            req.path ||
            req.url ||
            ''
        );
    };

    const handleSaveAsPdf = async (fileUrl, rawFileName, itemId = null) => {
        if (!fileUrl) {
            alert("No PDF file URL is available for this item yet.");
            return;
        }

        const cleanBase = (rawFileName || 'Document').trim();
        const fileName = cleanBase.toLowerCase().endsWith('.pdf') ? cleanBase : `${cleanBase}.pdf`;

        if (itemId) setSavingPdfId(itemId);
        try {
            const response = await fetch(fileUrl);
            if (!response.ok) throw new Error('Network response was not ok');
            const rawBlob = await response.blob();
            const pdfBlob = new Blob([rawBlob], { type: 'application/pdf' });
            const blobUrl = window.URL.createObjectURL(pdfBlob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);
        } catch (err) {
            console.warn("Direct blob download fallback to new tab:", err.message);
            window.open(fileUrl, '_blank', 'noopener,noreferrer');
        } finally {
            if (itemId) setSavingPdfId(null);
        }
    };

    const handleFileUpload = async (event) => {
        const files = event.target.files;
        if (!files || files.length === 0 || !clientData || !selectedFolderYear) return;
        setUploading(true);
        try {
            const clientDocRef = doc(db, 'clients', clientData.id);
            const uploadedNames = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                uploadedNames.push(file.name);
                const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
                const uniqueName = Date.now() + '_' + safeName;
                const storagePath = 'clients/' + clientData.id + '/Documents/' + selectedFolderYear + '/' + uniqueName;
                const storageRef = ref(storage, storagePath);
                await uploadBytes(storageRef, file);
                const downloadUrl = await getDownloadURL(storageRef);
                await updateDoc(clientDocRef, {
                    ['documents.' + selectedFolderYear]: arrayUnion({
                        name: file.name,
                        path: downloadUrl,
                        uploadedAt: new Date().toISOString(),
                        uploadedBy: 'Client Portal',
                        size: (file.size / 1024).toFixed(1) + ' KB',
                        engagement: selectedTag
                    }),
                    history: arrayUnion({
                        user: 'Client Portal',
                        timestamp: new Date().toLocaleString(),
                        action: `Client uploaded '${file.name}' (${selectedTag}) for ${selectedFolderYear}.`
                    })
                });
            }

            try {
                await addDoc(collection(db, 'firmNotifications'), {
                    type: 'upload',
                    title: `Uploaded ${uploadedNames.length} File(s) (${selectedFolderYear})`,
                    message: `${displayClientName} uploaded: ${uploadedNames.join(', ')} (${selectedTag})`,
                    clientId: clientData.id,
                    clientName: displayClientName,
                    isRead: false,
                    createdAt: serverTimestamp()
                });
            } catch (e) { }

            alert("File(s) uploaded successfully.");
        } catch (error) {
            alert("Upload failed. Please try again.");
        } finally {
            setUploading(false); event.target.value = null;
        }
    };

    const handleSendMessage = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const strippedText = newMessage.replace(/(<([^>]+)>)/gi, "").trim();
        if (!strippedText || !clientData) return;

        const messageHtml = newMessage;
        setNewMessage('');
        try {
            let senderName = clientData.name || 'Client';
            if (clientData.firstName) senderName = [clientData.firstName, clientData.lastName].filter(Boolean).join(' ').trim();
            if (clientData.clientType === 'Corporate') senderName = clientData.legalName || senderName;

            await addDoc(collection(db, 'clients', clientData.id, 'messages'), {
                text: messageHtml,
                sender: 'client',
                senderName: senderName,
                timestamp: serverTimestamp(),
                readByFirm: false
            });

            try {
                await addDoc(collection(db, 'firmNotifications'), {
                    type: 'message',
                    title: 'New Portal Message',
                    message: `${senderName}: ${strippedText.slice(0, 120)}`,
                    clientId: clientData.id,
                    clientName: senderName,
                    isRead: false,
                    createdAt: serverTimestamp()
                });
            } catch (e) { }
        } catch (error) {
            alert("Failed to send message.");
            setNewMessage(messageHtml);
        }
    };

    const handleCompleteTask = async (taskId) => {
        try {
            const matchedTask = clientTasks.find(t => t.id === taskId);
            const taskRef = doc(db, 'clients', clientData.id, 'clientTasks', taskId);
            await updateDoc(taskRef, {
                status: 'completed',
                completedAt: serverTimestamp()
            });

            try {
                await updateDoc(doc(db, 'clients', clientData.id), {
                    history: arrayUnion({
                        user: 'Client Portal',
                        timestamp: new Date().toLocaleString(),
                        action: `Client completed To-Do task: '${matchedTask?.title || 'Task'}'.`
                    })
                });
            } catch (e) { }

            try {
                await addDoc(collection(db, 'firmNotifications'), {
                    type: 'task',
                    title: 'Client Completed To-Do Item',
                    message: `${displayClientName} completed task: "${matchedTask?.title || 'Task'}"`,
                    clientId: clientData.id,
                    clientName: displayClientName,
                    isRead: false,
                    createdAt: serverTimestamp()
                });
            } catch (e) { }
        } catch (error) {
            alert("Failed to mark task as complete.");
        }
    };

    // --- PREPARER AVAILABILITY, 24H MINIMUM NOTICE & DOUBLE-BOOKING PROTECTION ---
    const getActivePreparerSchedule = () => {
        const prepKey = (clientData?.preparer || '').toLowerCase().trim();
        const officeKey = (clientData?.preferredOffice || '').toLowerCase().trim();
        const matched =
            preparerAvailabilityMap[prepKey] ||
            preparerAvailabilityMap[clientData?.preparer] ||
            preparerAvailabilityMap[officeKey] ||
            preparerAvailabilityMap.default ||
            {};

        const allowedTypes =
            Array.isArray(matched.allowedAppointmentTypes) && matched.allowedAppointmentTypes.length > 0
                ? matched.allowedAppointmentTypes
                : ['Phone Call', 'Video Consultation', 'In-Office Meeting'];

        return {
            availableDays: Array.isArray(matched.availableDays) ? matched.availableDays : [1, 2, 3, 4, 5],
            startTime: matched.startTime || '09:00',
            endTime: matched.endTime || '17:00',
            breakEnabled: matched.breakEnabled !== false,
            breakStart: matched.breakStart || '12:00',
            breakEnd: matched.breakEnd || '13:00',
            minNoticeHours: Math.max(24, Number(matched.minNoticeHours || 24)),
            allowedAppointmentTypes: allowedTypes,
            blockedDates: Array.isArray(matched.blockedDates) ? matched.blockedDates : []
        };
    };

    const isSlotBookedOnCrmCalendar = (dateStr, timeStr) => {
        if (!dateStr || !timeStr) return true;
        const schedule = getActivePreparerSchedule();
        const slotStart = new Date(`${dateStr}T${timeStr}:00`);
        const slotEnd = new Date(slotStart.getTime() + 30 * 60 * 1000);

        // 1. Enforce minimum advance notice (at least 24 hours from current time)
        const minNoticeMs = Math.max(24, Number(schedule.minNoticeHours || 24)) * 60 * 60 * 1000;
        if (slotStart.getTime() < Date.now() + minNoticeMs) {
            return true;
        }

        const targetPrep = (clientData?.preparer || '').toLowerCase().trim();
        const targetOffice = (clientData?.preferredOffice || '').toLowerCase().trim();

        // 2. Check Preparer's CRM Calendar appointments (ignores Cancelled / Declined / Reschedule Requested)
        const crmConflict = allCalendarAppointments.some((appt) => {
            if (
                !appt.start ||
                appt.status === 'Cancelled' ||
                appt.status === 'Declined' ||
                appt.status === 'Reschedule Requested'
            ) {
                return false;
            }

            const apptAssigned = String(appt.assignedTo || appt.resourceId || '').toLowerCase().trim();
            const apptOffice = String(appt.office || '').toLowerCase().trim();

            const matchesPreparer =
                !targetPrep ||
                !apptAssigned ||
                apptAssigned === targetPrep ||
                (targetOffice && apptOffice === targetOffice);

            if (!matchesPreparer) return false;

            const apptStart = new Date(appt.start);
            const apptEnd = appt.end ? new Date(appt.end) : new Date(apptStart.getTime() + 30 * 60 * 1000);
            return slotStart < apptEnd && slotEnd > apptStart;
        });
        if (crmConflict) return true;

        // 3. Check Preparer's synced external Outlook / Google Calendar busy blocks
        const externalConflict = externalBusySlots.some((busy) => {
            if (!busy.start) return false;
            const busyOwner = String(busy.office || '').toLowerCase().trim();
            if (targetPrep && busyOwner && busyOwner !== targetPrep && busyOwner !== targetOffice) {
                return false;
            }
            const busyStart = new Date(busy.start);
            const busyEnd = busy.end ? new Date(busy.end) : new Date(busyStart.getTime() + 30 * 60 * 1000);
            return slotStart < busyEnd && slotEnd > busyStart;
        });
        return externalConflict;
    };

    const getOpenAvailableSlotsForDate = (dateStr) => {
        if (!dateStr) return [];
        const schedule = getActivePreparerSchedule();

        if (schedule.blockedDates.includes(dateStr)) {
            return [];
        }

        const [y, m, d] = dateStr.split('-').map(Number);
        const dayOfWeek = new Date(y, m - 1, d).getDay();
        if (!schedule.availableDays.includes(dayOfWeek)) {
            return [];
        }

        const toMinutes = (hhmm) => {
            const [hh, mm] = String(hhmm || '09:00').split(':').map(Number);
            return hh * 60 + (mm || 0);
        };

        const startMins = toMinutes(schedule.startTime);
        const endMins = toMinutes(schedule.endTime);
        const breakStartMins = toMinutes(schedule.breakStart);
        const breakEndMins = toMinutes(schedule.breakEnd);

        const openSlots = [];
        for (let mins = startMins; mins + 30 <= endMins; mins += 30) {
            if (schedule.breakEnabled && mins < breakEndMins && mins + 30 > breakStartMins) {
                continue;
            }

            const hh = String(Math.floor(mins / 60)).padStart(2, '0');
            const mm = String(mins % 60).padStart(2, '0');
            const value = `${hh}:${mm}`;

            if (!isSlotBookedOnCrmCalendar(dateStr, value)) {
                const hourNum = Math.floor(mins / 60);
                const suffix = hourNum >= 12 ? 'PM' : 'AM';
                const hour12 = hourNum % 12 === 0 ? 12 : hourNum % 12;
                openSlots.push({
                    value,
                    label: `${hour12}:${mm} ${suffix}`
                });
            }
        }
        return openSlots;
    };

    const handleRequestAppointment = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!bookingDate || !bookingTimeSlot || !clientData) return;

        const schedule = getActivePreparerSchedule();
        const allowedTypes = schedule.allowedAppointmentTypes;
        const effectiveType = allowedTypes.includes(bookingType) ? bookingType : (allowedTypes[0] || 'Phone Call');

        const openSlots = getOpenAvailableSlotsForDate(bookingDate);
        if (!openSlots.some((s) => s.value === bookingTimeSlot)) {
            alert('Please select one of the available time slots shown for this date.');
            return;
        }

        setSubmittingBooking(true);
        try {
            const startDateTime = new Date(`${bookingDate}T${bookingTimeSlot}:00`);
            const endDateTime = new Date(startDateTime.getTime() + 30 * 60 * 1000);
            const clientPhone = clientData.phone1 || clientData.corpPhone || 'N/A';
            const clientEmail = clientData.email || clientData.corpEmail || currentUser?.email || '';

            const apptPayload = {
                clientId: clientData.id,
                clientName: displayClientName,
                title: `[Requested] ${effectiveType}: ${displayClientName}${bookingTopic.trim() ? ` (${bookingTopic.trim()})` : ''}`,
                description: `Requested via Client Portal (Pending Confirmation)\nType: ${effectiveType}\nClient: ${displayClientName}\nPhone: ${clientPhone}\nEmail: ${clientEmail}\nTopic: ${bookingTopic.trim() || 'General Consultation'}`,
                meetingType: effectiveType,
                topic: bookingTopic.trim(),
                start: startDateTime.toISOString(),
                end: endDateTime.toISOString(),
                office: clientData.preferredOffice || 'Main Office',
                assignedTo: clientData.preparer || clientData.preferredOffice || '',
                status: 'Requested',
                requestedByClient: true,
                reviewedByStaff: false,
                createdBy: 'Client Portal',
                createdAt: new Date().toISOString()
            };

            const apptDocRef = await addDoc(collection(db, 'appointments'), apptPayload);

            const formattedDateLabel = startDateTime.toLocaleString([], {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });

            await updateDoc(doc(db, 'clients', clientData.id), {
                history: arrayUnion({
                    user: 'Client Portal',
                    timestamp: new Date().toLocaleString(),
                    action: `Requested a ${effectiveType} for ${formattedDateLabel} (Pending Firm Confirmation).`
                })
            });

            await addDoc(collection(db, 'firmNotifications'), {
                type: 'appointment',
                appointmentId: apptDocRef.id,
                appointmentStatus: 'Requested',
                meetingType: effectiveType,
                requestedStart: startDateTime.toISOString(),
                title: `Confirm ${effectiveType} Request`,
                message: `${displayClientName} requested ${formattedDateLabel}${bookingTopic.trim() ? ` — "${bookingTopic.trim()}"` : ''}`,
                clientId: clientData.id,
                clientName: displayClientName,
                isRead: false,
                createdAt: serverTimestamp()
            });

            setBookingTopic('');
            alert(`Your ${effectiveType} request for ${formattedDateLabel} has been submitted and is pending confirmation from your preparer.`);
        } catch (err) {
            console.error('Error requesting appointment:', err);
            alert('Failed to submit appointment request. Please try again.');
        } finally {
            setSubmittingBooking(false);
        }
    };

    const handleCancelClientAppointment = async (apptId, skipConfirm = false) => {
        if (!skipConfirm && !window.confirm('Cancel this appointment request?')) return;
        try {
            await deleteDoc(doc(db, 'appointments', apptId));
        } catch (err) {
            alert('Could not cancel appointment.');
        }
    };

    if (loadingWorkspace) {
        return (
            <div className="flex h-screen items-center justify-center bg-white text-gray-500 font-sans">
                Loading portal...
            </div>
        );
    }

    if (!clientData) {
        return (
            <div className="flex h-screen items-center justify-center bg-gray-50 p-4 font-sans">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center space-y-4">
                    <div className="w-12 h-12 rounded-full bg-blue-50 text-[#0078d4] flex items-center justify-center mx-auto text-xl font-bold">
                        !
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">
                        No Client Portal Workspace Found
                    </h2>
                    <p className="text-sm text-gray-600 leading-relaxed">
                        You are currently signed in as <strong>{currentUser?.email}</strong>, which is not linked to a client file in the portal.
                    </p>
                    {workspaceError && (
                        <p className="text-xs text-red-600 bg-red-50 p-2.5 rounded-lg border border-red-200">
                            {workspaceError}
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={handleLogout}
                        className="w-full py-2.5 px-4 bg-[#0078d4] hover:bg-[#005a9e] text-white font-bold rounded-lg shadow transition-colors text-sm"
                    >
                        Sign Out & Switch to Client Account
                    </button>
                </div>
            </div>
        );
    }

    const availableYears = clientData.filingStatus ? Object.keys(clientData.filingStatus).sort((a, b) => b - a) : [new Date().getFullYear().toString()];

    let visibleDocuments = selectedFolderYear ? (clientData.documents?.[selectedFolderYear] || []) : [];
    if (searchQuery.trim()) {
        visibleDocuments = visibleDocuments.filter(d => d.name.toLowerCase().includes(searchQuery.toLowerCase()) || (d.engagement && d.engagement.toLowerCase().includes(searchQuery.toLowerCase())));
    }

    let displayClientName = 'Client';
    if (clientData.clientType === 'Corporate') displayClientName = clientData.legalName || clientData.name;
    else if (clientData.clientType === 'Trust') displayClientName = clientData.trustName || clientData.name;
    else if (clientData.clientType === 'Partnership') displayClientName = clientData.partnershipName || clientData.name;
    else displayClientName = clientData.firstName ? [clientData.firstName, clientData.lastName].filter(Boolean).join(' ').trim() : (clientData.name || 'Client');

    const clientInitials = displayClientName.substring(0, 2).toUpperCase();
    const unreadCount = messages.filter(m => m.sender === 'firm' && !m.readByClient).length;

    const pendingSignaturesCount = signatureRequests.filter(r => r.status === 'pending').length;
    const pendingTasksCount = clientTasks.filter(t => (t.status || 'pending') === 'pending').length;
    const totalPendingActions = pendingSignaturesCount + pendingTasksCount;

    const outstandingBalance = Object.values(clientData.invoices || {})
        .flat()
        .filter(inv => inv && inv.type !== 'Retainer' && inv.invoiceNumber !== 'RETAINER' && inv.status !== 'Paid')
        .reduce((sum, inv) => sum + parseFloat(inv.balance ?? inv.total ?? 0), 0);

    const getInvoiceBranding = (inv) => {
        if (inv?.brandingSnapshot) {
            const snap = inv.brandingSnapshot;
            const showLogo = snap.showLogo !== false && Boolean(snap.logoBase64);
            return {
                companyName: snap.companyName || 'NVS',
                address: snap.address || '',
                phone: snap.phone || '',
                email: snap.email || '',
                website: snap.website || '',
                taxNumber: snap.taxNumber || '',
                logoBase64: showLogo ? snap.logoBase64 : '',
                headerColor: snap.headerColor || '#0078d4',
                bankDetails: snap.bankDetails || '',
                termsAndConditions: snap.termsAndConditions || '',
                footerText: snap.footerText || 'Thank you for your business!',
                preparerDisplayName: snap.preparerDisplayName || inv.user || '',
                showLogo,
                showFirmName: snap.showFirmName !== false,
                showFirmAddress: snap.showFirmAddress !== false,
                showFirmContact: snap.showFirmContact !== false,
                showFirmWebsite: snap.showFirmWebsite !== false,
                showTaxNumber: snap.showTaxNumber !== false,
                showPreparer: snap.showPreparer !== false,
                showPaymentTakenBy: snap.showPaymentTakenBy !== false,
                showBankDetails: snap.showBankDetails !== false,
                showTerms: snap.showTerms !== false
            };
        }

        const prep = inv?.user;
        const prepTemplate = (prep && firmTemplate?.preparerTemplates?.[prep] && firmTemplate.preparerTemplates[prep].useCustomOverride)
            ? firmTemplate.preparerTemplates[prep]
            : {};
        const merged = { ...(firmTemplate || {}), ...prepTemplate };
        const showLogo = merged.showLogo !== false && !merged.logoRemoved && firmTemplate?.showLogo !== false && !firmTemplate?.logoRemoved;
        return {
            companyName: merged.companyName || merged.appName || 'NVS',
            address: merged.address || 'M145 - 1460 The Queensway, Toronto, ON, Canada',
            phone: merged.phone || '',
            email: merged.email || 'nestor@nvstax.com',
            website: merged.website || '',
            taxNumber: merged.taxNumber || '',
            logoBase64: showLogo ? (merged.logoBase64 || '') : '',
            headerColor: merged.headerColor || '#0078d4',
            bankDetails: merged.bankDetails || 'To complete your payment, please send an e-Transfer to nestor@nvstax.com or follow the payment link sent to your email.',
            termsAndConditions: merged.termsAndConditions || '',
            footerText: merged.footerText || merged.customFooterText || 'Thank you for your business!',
            preparerDisplayName: inv?.user || '',
            showLogo: showLogo,
            showFirmName: merged.showFirmName !== false,
            showFirmAddress: merged.showFirmAddress !== false,
            showFirmContact: merged.showFirmContact !== false,
            showFirmWebsite: merged.showFirmWebsite !== false,
            showTaxNumber: merged.showTaxNumber !== false,
            showPreparer: merged.showPreparer !== false,
            showPaymentTakenBy: merged.showPaymentTakenBy !== false,
            showBankDetails: merged.showBankDetails !== false,
            showTerms: merged.showTerms !== false
        };
    };

    const handleSaveInvoiceAsPdf = (inv) => {
        if (!inv) return;
        const branding = getInvoiceBranding(inv);
        const billedName = inv.clientSnapshot?.name || displayClientName;
        const billedEmail = inv.clientSnapshot?.email || clientData.email || '';
        const billedAddress = inv.clientSnapshot?.addressFormatted || '';

        const invTotal = parseFloat(inv.total || 0);
        const invTax = parseFloat(inv.taxAmount ?? inv.tax ?? 0);
        const invSubtotal = inv.subtotal !== undefined ? parseFloat(inv.subtotal) : (invTotal - invTax);
        const invBalance = inv.balance !== undefined ? parseFloat(inv.balance) : (inv.status === 'Paid' ? 0 : invTotal);
        const hasPaymentsList = Array.isArray(inv.payments) && inv.payments.length > 0;
        const invPaid = hasPaymentsList
            ? inv.payments.reduce((sum, p) => sum + (parseFloat(p.amountReceived ?? p.amount) || 0), 0)
            : Math.max(0, invTotal - invBalance);

        const isPaidStatus = inv.status === 'Paid' || (invTotal > 0 && invBalance <= 0.005);
        const isPartialStatus = !isPaidStatus && (inv.status === 'Partially Paid' || invPaid > 0);
        const docHeaderTitle = inv.type === 'Retainer' ? 'Retainer Receipt' : (isPaidStatus ? 'Receipt' : (isPartialStatus ? 'Partial Receipt' : 'Invoice'));
        const defaultInvYear = String(inv.year || new Date(inv.invoiceDate || inv.id || Date.now()).getFullYear());

        const rowsHtml = (inv.items && inv.items.length > 0)
            ? inv.items.map(item => `
                <tr>
                    <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb;">${item.year || defaultInvYear}</td>
                    <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb;">${item.description || 'Professional Services'}</td>
                    <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb; text-align: right;">$${parseFloat(item.amount ?? item.price ?? 0).toFixed(2)}</td>
                </tr>
            `).join('')
            : `
                <tr>
                    <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb;">${defaultInvYear}</td>
                    <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb;">Professional Services</td>
                    <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb; text-align: right;">$${invSubtotal.toFixed(2)}</td>
                </tr>
            `;

        const printWin = window.open('', '_blank', 'width=850,height=900');
        if (!printWin) {
            alert('Please allow pop-ups in your browser to save this invoice as a PDF.');
            return;
        }

        printWin.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8" />
                <title>${docHeaderTitle} #${inv.invoiceNumber || inv.id}</title>
                <style>
                    body { font-family: Arial, sans-serif; color: #374151; padding: 40px; line-height: 1.5; }
                    .top { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; }
                    .firm-title { font-size: 26px; font-weight: 900; color: ${branding.headerColor}; }
                    .doc-title { font-size: 24px; font-weight: 800; text-transform: uppercase; color: ${branding.headerColor}; text-align: right; }
                    table { width: 100%; border-collapse: collapse; margin-top: 24px; font-size: 13px; }
                    th { background: #f9fafb; padding: 8px 10px; text-align: left; border-top: 1px solid #e5e7eb; border-bottom: 1px solid #e5e7eb; }
                    .summary { margin-top: 24px; display: flex; justify-content: space-between; gap: 32px; }
                    .totals { width: 260px; margin-left: auto; font-size: 13px; }
                    .totals-row { display: flex; justify-content: space-between; padding: 4px 0; }
                    .balance-due { font-size: 16px; font-weight: 800; color: ${branding.headerColor}; border-top: 2px solid ${branding.headerColor}; padding-top: 8px; margin-top: 8px; }
                </style>
            </head>
            <body>
                <div class="top">
                    <div>
                        ${branding.showLogo && branding.logoBase64 ? `<img src="${branding.logoBase64}" style="max-height:60px; max-width:160px; margin-bottom:8px;" />` : ''}
                        ${branding.showFirmName ? `<div class="firm-title">${branding.companyName}</div>` : ''}
                        ${branding.showFirmAddress && branding.address ? `<div style="font-size:12px; color:#6b7280;">${branding.address}</div>` : ''}
                        ${branding.showFirmContact && (branding.phone || branding.email) ? `<div style="font-size:12px; color:#6b7280;">${[branding.phone, branding.email].filter(Boolean).join(' | ')}</div>` : ''}
                        ${branding.showFirmWebsite && branding.website ? `<div style="font-size:12px; color:#6b7280;">${branding.website}</div>` : ''}
                        ${branding.showTaxNumber && branding.taxNumber ? `<div style="font-size:11px; color:#6b7280; margin-top:4px;">Tax #: ${branding.taxNumber}</div>` : ''}
                    </div>
                    <div style="text-align: right; font-size: 13px;">
                        <div class="doc-title">${docHeaderTitle}</div>
                        <div style="margin-top:8px;"><strong>#${inv.invoiceNumber || inv.id}</strong></div>
                        <div><strong>Date:</strong> ${new Date(inv.invoiceDate || inv.id).toLocaleDateString()}</div>
                        <div><strong>Status:</strong> ${inv.status || 'Pending'}</div>
                        ${branding.showPreparer && (branding.preparerDisplayName || inv.user) ? `<div style="font-size:11px; color:#6b7280;">Prepared By: ${branding.preparerDisplayName || inv.user}</div>` : ''}
                    </div>
                </div>
                <div style="margin-top: 20px; font-size: 13px;">
                    <strong>Billed To:</strong><br/>
                    ${billedName}<br/>
                    ${billedAddress ? `${billedAddress}<br/>` : ''}
                    ${billedEmail}
                </div>
                <table>
                    <thead>
                        <tr>
                            <th style="width: 20%;">Year</th>
                            <th style="width: 55%;">Service Details</th>
                            <th style="width: 25%; text-align: right;">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rowsHtml}
                    </tbody>
                </table>
                <div class="summary">
                    <div style="flex: 1; font-size: 12px; color: #6b7280;">
                        ${branding.showBankDetails && branding.bankDetails ? `<p><strong>Payment Instructions:</strong><br/>${branding.bankDetails}</p>` : ''}
                        ${branding.showTerms && branding.termsAndConditions ? `<p><strong>Terms & Conditions:</strong><br/>${branding.termsAndConditions}</p>` : ''}
                    </div>
                    <div class="totals">
                        <div class="totals-row"><span>Subtotal:</span><span>$${invSubtotal.toFixed(2)}</span></div>
                        <div class="totals-row"><span>Tax (GST/HST):</span><span>$${invTax.toFixed(2)}</span></div>
                        <div class="totals-row" style="font-weight:700; border-top:1px solid #e5e7eb; padding-top:6px;"><span>Total:</span><span>$${invTotal.toFixed(2)}</span></div>
                        ${invPaid > 0 ? `<div class="totals-row" style="color:#16a34a;"><span>Amount Paid:</span><span>-$${invPaid.toFixed(2)}</span></div>` : ''}
                        <div class="totals-row balance-due"><span>Balance Due:</span><span>$${invBalance.toFixed(2)}</span></div>
                    </div>
                </div>
                ${branding.footerText ? `<div style="margin-top:40px; text-align:center; font-size:11px; color:#9ca3af; font-style:italic;">${branding.footerText}</div>` : ''}
                <script>
                    window.onload = function() {
                        window.print();
                    };
                </script>
            </body>
            </html>
        `);
        printWin.document.close();
    };

    const activeBranding = viewingInvoice ? getInvoiceBranding(viewingInvoice) : null;

    return (
        <div className="flex h-screen bg-white text-gray-800 font-sans relative">
            <div className="w-[260px] border-r border-gray-200 flex flex-col items-center pt-8 bg-white z-10">
                <div className="cursor-pointer relative flex flex-col items-center mb-8 hover:opacity-90 transition-opacity" onClick={() => setShowFirmInfo(true)}>
                    <div className="bg-white border shadow-sm px-6 py-3 font-black text-4xl tracking-tighter text-black mb-[-12px] z-10 relative">NVS</div>
                    <div className="flex items-center gap-1 z-20 absolute -bottom-4 right-0">
                        <div className="bg-[#0078d4] text-white w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 border-white">{clientInitials}</div>
                    </div>
                </div>

                {accessibleWorkspaces.length > 1 && (
                    <div className="w-full px-6 mb-2 mt-4">
                        <label className="block text-[10px] font-bold text-gray-400 mb-1 uppercase tracking-widest">Active Workspace</label>
                        <select
                            value={clientData.id}
                            onChange={handleWorkspaceChange}
                            className="w-full p-2 border border-gray-300 rounded text-sm bg-gray-50 focus:border-[#0078d4] outline-none text-gray-800 font-semibold shadow-sm cursor-pointer"
                        >
                            {accessibleWorkspaces.map(ws => {
                                let optName = ws.name || 'Client';
                                if (ws.clientType === 'Corporate') optName = ws.legalName || optName;
                                else if (ws.clientType === 'Trust') optName = ws.trustName || optName;
                                else if (ws.clientType === 'Partnership') optName = ws.partnershipName || optName;
                                else optName = ws.firstName ? [ws.firstName, ws.lastName].filter(Boolean).join(' ').trim() : optName;

                                return (
                                    <option key={ws.id} value={ws.id}>
                                        {optName} ({ws.clientType || 'Personal'})
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                )}

                <nav className="w-full mt-4 flex flex-col">
                    <button onClick={() => { setActiveTab('Home'); setSelectedFolderYear(null); }} className={activeTab === 'Home' ? 'flex items-center gap-3 px-6 py-3 w-full text-sm font-semibold transition-colors text-[#0078d4] border-l-4 border-[#0078d4] bg-blue-50/50' : 'flex items-center gap-3 px-6 py-3 w-full text-sm font-semibold transition-colors text-gray-700 hover:bg-gray-50 border-l-4 border-transparent'}>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                        HOME
                    </button>
                    <button onClick={() => { setActiveTab('Documents'); }} className={activeTab === 'Documents' ? 'flex items-center gap-3 px-6 py-3 w-full text-sm font-semibold transition-colors text-[#0078d4] border-l-4 border-[#0078d4] bg-blue-50/50' : 'flex items-center gap-3 px-6 py-3 w-full text-sm font-semibold transition-colors text-gray-700 hover:bg-gray-50 border-l-4 border-transparent'}>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                        DOCUMENTS
                    </button>
                    <button onClick={() => { setActiveTab('Billing'); setSelectedFolderYear(null); }} className={activeTab === 'Billing' ? 'flex items-center gap-3 px-6 py-3 w-full text-sm font-semibold transition-colors text-[#0078d4] border-l-4 border-[#0078d4] bg-blue-50/50' : 'flex items-center gap-3 px-6 py-3 w-full text-sm font-semibold transition-colors text-gray-700 hover:bg-gray-50 border-l-4 border-transparent'}>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" /></svg>
                        BILLING & INVOICES
                    </button>
                    <button onClick={() => { setActiveTab('ActionItems'); setSelectedFolderYear(null); }} className={activeTab === 'ActionItems' ? 'flex items-center gap-3 px-6 py-3 w-full text-sm font-semibold transition-colors text-[#0078d4] border-l-4 border-[#0078d4] bg-blue-50/50' : 'flex items-center gap-3 px-6 py-3 w-full text-sm font-semibold transition-colors text-gray-700 hover:bg-gray-50 border-l-4 border-transparent'}>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                        ACTION ITEMS
                        {totalPendingActions > 0 && <span className="ml-auto bg-red-600 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">{totalPendingActions}</span>}
                    </button>
                    <button onClick={() => { setActiveTab('UnreadFiles'); setSelectedFolderYear(null); }} className={activeTab === 'UnreadFiles' ? 'flex items-center gap-3 px-6 py-3 w-full text-sm font-semibold transition-colors text-[#0078d4] border-l-4 border-[#0078d4] bg-blue-50/50' : 'flex items-center gap-3 px-6 py-3 w-full text-sm font-semibold transition-colors text-gray-700 hover:bg-gray-50 border-l-4 border-transparent'}>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
                        MESSAGES
                        {unreadCount > 0 && <span className="ml-auto bg-[#0078d4] text-white text-[10px] px-2 py-0.5 rounded-full font-bold">{unreadCount}</span>}
                    </button>
                    <button onClick={() => { setActiveTab('BookCall'); setSelectedFolderYear(null); }} className={activeTab === 'BookCall' ? 'flex items-center gap-3 px-6 py-3 w-full text-sm font-semibold transition-colors text-[#0078d4] border-l-4 border-[#0078d4] bg-blue-50/50' : 'flex items-center gap-3 px-6 py-3 w-full text-sm font-semibold transition-colors text-gray-700 hover:bg-gray-50 border-l-4 border-transparent'}>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        BOOK A CALL
                    </button>
                    <button onClick={handleLogout} className="mt-auto mb-8 mx-6 px-4 py-2 text-xs font-semibold text-gray-500 hover:text-gray-800 transition-colors">Sign Out</button>
                </nav>
            </div>

            <div className="flex-1 flex flex-col h-full bg-[#f8f9fa] overflow-y-auto">
                <div className="p-6 max-w-[1200px] w-full mx-auto">
                    {activeTab === 'Home' && (
                        <div className="animate-fade-in">
                            <div className="mb-6 flex justify-between items-end">
                                <div>
                                    <h1 className="text-3xl font-bold text-gray-800 mb-1">Welcome, {displayClientName}</h1>
                                    <p className="text-gray-500 text-sm">Manage your documents and communications securely.</p>
                                </div>
                            </div>

                            <div className="bg-[#0078d4] text-white p-3 font-semibold text-sm rounded-t-sm">Tasks for your attention</div>
                            <div className="bg-white border border-gray-200 border-t-0 p-3 flex flex-wrap justify-between items-center gap-2 text-sm mb-6 rounded-b-sm shadow-sm">
                                <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-[#0078d4]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    <span className="font-semibold">There {unreadCount === 1 ? 'is' : 'are'} {unreadCount} unread message(s) and {totalPendingActions} pending action item(s)</span>
                                </div>
                                <div className="flex gap-4">
                                    <button onClick={() => setActiveTab('BookCall')} className="text-[#0078d4] font-medium hover:underline">Book a call</button>
                                    <button onClick={() => setActiveTab('ActionItems')} className="text-[#0078d4] font-medium hover:underline">View action items</button>
                                    <button onClick={() => setActiveTab('UnreadFiles')} className="text-[#0078d4] font-medium hover:underline">View messages</button>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 p-6 text-sm text-gray-600 space-y-6 leading-relaxed shadow-sm">
                                <p>Welcome to Portal this is your secure, cloud-based portal for sharing documents with us. You can access it anytime, from any device, to upload and manage your files.</p>
                                <p>All files shared here are encrypted and saved in a 100% Canadian cloud environment.</p>
                                <p className="text-[#0078d4] hover:underline cursor-pointer pt-4 text-xs font-semibold">Client Guide Video</p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'Documents' && (
                        <div className="animate-fade-in space-y-4">
                            <h2 className="text-2xl font-semibold text-gray-800">Documents for {displayClientName}</h2>

                            <div className="flex border border-gray-300 rounded bg-white overflow-hidden shadow-sm mb-4">
                                <input
                                    type="text" placeholder="Search for files or categories..."
                                    className="flex-1 p-2 outline-none text-sm"
                                    value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <button className="bg-[#0078d4] text-white px-4 flex items-center gap-2 text-sm hover:bg-[#005a9e] transition-colors">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                    Search
                                </button>
                            </div>

                            <div className="flex justify-between items-center text-sm mb-4">
                                <div className="flex items-center gap-2 text-[#0078d4] font-medium">
                                    <span className="hover:underline cursor-pointer" onClick={() => setSelectedFolderYear(null)}>Home</span>
                                    <svg className="w-3 h-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                    <span className={selectedFolderYear ? "hover:underline cursor-pointer" : "text-gray-800 font-semibold"} onClick={() => setSelectedFolderYear(null)}>Documents</span>
                                    {selectedFolderYear && (
                                        <>
                                            <svg className="w-3 h-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                            <span className="text-gray-800 font-semibold">{selectedFolderYear}</span>
                                        </>
                                    )}
                                </div>
                                {selectedFolderYear && (
                                    <div className="flex items-center gap-3">
                                        <select
                                            value={selectedTag}
                                            onChange={(e) => setSelectedTag(e.target.value)}
                                            className="text-sm border border-gray-300 rounded px-2 py-1.5 outline-none focus:border-[#0078d4] bg-white shadow-sm"
                                        >
                                            <option value="Source Docs">Source Docs</option>
                                            <option value="Authorization">Authorization</option>
                                            <option value="Tax Return">Tax Return</option>
                                            <option value="Workpapers">Workpapers</option>
                                            <option value="Permanent Docs">Permanent Docs</option>
                                            <option value="Notice">Notice</option>
                                            <option value="Bookkeeping">Bookkeeping</option>
                                            <option value="Payroll">Payroll</option>
                                            <option value="Billing">Billing</option>
                                            <option value="General Docs">General Docs</option>
                                        </select>
                                        <div className="relative">
                                            <input type="file" multiple onChange={handleFileUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" disabled={uploading} />
                                            <button className="bg-[#0078d4] text-white px-4 py-1.5 rounded flex items-center gap-2 hover:bg-[#005a9e] transition-colors shadow-sm">
                                                {uploading ? 'Uploading...' : 'Upload'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="bg-white border rounded-sm overflow-hidden text-sm shadow-sm">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-50 border-b text-gray-600">
                                        <tr>
                                            <th className="p-3 font-semibold">Name</th>
                                            <th className="p-3 font-semibold">Category</th>
                                            <th className="p-3 font-semibold">Created By</th>
                                            <th className="p-3 font-semibold">Date</th>
                                            <th className="p-3 font-semibold text-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {!selectedFolderYear && availableYears.map(year => (
                                            <tr key={year} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedFolderYear(year)}>
                                                <td className="p-3 flex items-center gap-2 text-[#0078d4] font-medium">
                                                    <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                                                    {year}
                                                </td>
                                                <td className="p-3 text-gray-500">-</td>
                                                <td className="p-3 text-gray-500">Firm Admin</td>
                                                <td className="p-3 text-gray-500">-</td>
                                                <td className="p-3 text-right text-[#0078d4] font-semibold text-xs">Open Folder →</td>
                                            </tr>
                                        ))}
                                        {selectedFolderYear && visibleDocuments.map((docItem, i) => {
                                            const docKey = docItem.path || `${docItem.name}_${i}`;
                                            return (
                                                <tr key={docKey} className="hover:bg-gray-50">
                                                    <td className="p-3 flex items-center gap-2">
                                                        <a href={docItem.path} target="_blank" rel="noopener noreferrer" className="text-[#0078d4] hover:underline truncate max-w-xs">{docItem.name}</a>
                                                    </td>
                                                    <td className="p-3">
                                                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                                                            {docItem.engagement || 'General Docs'}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 text-gray-500">{docItem.uploadedBy || 'Portal User'}</td>
                                                    <td className="p-3 text-gray-500">{new Date(docItem.uploadedAt).toLocaleDateString()}</td>
                                                    <td className="p-3 text-right">
                                                        <div className="inline-flex items-center justify-end gap-2">
                                                            <a
                                                                href={docItem.path}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs rounded transition-colors"
                                                            >
                                                                View
                                                            </a>
                                                            <button
                                                                onClick={() => handleSaveAsPdf(docItem.path, docItem.name, docKey)}
                                                                disabled={savingPdfId === docKey}
                                                                className="px-3 py-1 bg-[#0078d4] hover:bg-[#005a9e] text-white font-semibold text-xs rounded transition-colors shadow-sm"
                                                            >
                                                                {savingPdfId === docKey ? 'Saving...' : 'Save as PDF'}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {selectedFolderYear && visibleDocuments.length === 0 && (
                                            <tr><td colSpan="5" className="p-8 text-center text-gray-500">No files to display.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === 'Billing' && (
                        <div className="animate-fade-in space-y-6">
                            <h2 className="text-2xl font-semibold text-gray-800">Billing & Invoices</h2>
                            <p className="text-sm text-gray-600">Review past invoices and payment receipts for {displayClientName}.</p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="bg-white p-5 border rounded-sm shadow-sm">
                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Retainer Balance</p>
                                    <p className="text-2xl font-bold text-green-600 mt-1">${parseFloat(clientData.retainerBalance || 0).toFixed(2)}</p>
                                </div>
                                <div className="bg-white p-5 border rounded-sm shadow-sm">
                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Outstanding Balance</p>
                                    <p className="text-2xl font-bold text-gray-900 mt-1">
                                        ${outstandingBalance.toFixed(2)}
                                    </p>
                                </div>
                            </div>

                            <div className="bg-white border rounded-sm shadow-sm overflow-hidden">
                                {!clientData.invoices || Object.keys(clientData.invoices).length === 0 ? (
                                    <div className="p-10 text-center text-gray-500"><p>No invoices or billing records found.</p></div>
                                ) : (
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-gray-50 border-b text-gray-600">
                                            <tr>
                                                <th className="p-4 font-semibold">Invoice #</th>
                                                <th className="p-4 font-semibold">Year</th>
                                                <th className="p-4 font-semibold">Date</th>
                                                <th className="p-4 font-semibold">Total</th>
                                                <th className="p-4 font-semibold">Balance</th>
                                                <th className="p-4 font-semibold">Status</th>
                                                <th className="p-4 font-semibold text-right">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {Object.entries(clientData.invoices).flatMap(([year, invs]) => (invs || []).map(inv => ({ ...inv, year }))).map(inv => {
                                                const invTotal = parseFloat(inv.total || 0);
                                                const invBalance = inv.balance !== undefined ? parseFloat(inv.balance) : (inv.status === 'Paid' ? 0 : invTotal);
                                                return (
                                                    <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="p-4 font-medium text-gray-800">#{inv.invoiceNumber || inv.id}</td>
                                                        <td className="p-4 text-gray-600">{inv.year}</td>
                                                        <td className="p-4 text-gray-600">{new Date(inv.invoiceDate || inv.id).toLocaleDateString()}</td>
                                                        <td className="p-4 font-semibold text-gray-800">${invTotal.toFixed(2)}</td>
                                                        <td className="p-4 font-semibold text-gray-800">${invBalance.toFixed(2)}</td>
                                                        <td className="p-4">
                                                            <span className={inv.status === 'Paid' ? 'px-2.5 py-1 text-xs font-bold rounded bg-green-100 text-green-800' : 'px-2.5 py-1 text-xs font-bold rounded bg-amber-100 text-amber-800'}>
                                                                {inv.status || 'Pending'}
                                                            </span>
                                                        </td>
                                                        <td className="p-4 text-right">
                                                            <div className="inline-flex items-center justify-end gap-2">
                                                                <button onClick={() => setViewingInvoice(inv)} className="px-3.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium text-xs rounded transition-colors">
                                                                    View
                                                                </button>
                                                                <button onClick={() => handleSaveInvoiceAsPdf(inv)} className="px-3.5 py-1.5 bg-[#0078d4] text-white font-medium text-xs rounded hover:bg-[#005a9e] transition-colors shadow-sm">
                                                                    Save as PDF
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'ActionItems' && (
                        <div className="animate-fade-in space-y-8">
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-800">Action Items</h2>
                                <p className="text-sm text-gray-600 mb-4">Review requests, required uploads, and signatures needed for {displayClientName}.</p>
                            </div>

                            <div className="bg-white border rounded-sm shadow-sm overflow-hidden mb-6">
                                <div className="bg-gray-50 border-b p-4 font-bold text-gray-700 flex items-center gap-2">
                                    <svg className="w-5 h-5 text-[#0078d4]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                                    To-Do List
                                </div>
                                {clientTasks.length === 0 ? (
                                    <div className="p-8 text-center text-gray-500 text-sm">
                                        <p>You have no pending tasks to complete.</p>
                                    </div>
                                ) : (
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-white border-b text-gray-600 text-xs uppercase tracking-wider">
                                            <tr>
                                                <th className="p-4 font-semibold w-1/2">Task Description</th>
                                                <th className="p-4 font-semibold">Status</th>
                                                <th className="p-4 font-semibold text-right">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {clientTasks.map(task => {
                                                const isPending = (task.status || 'pending') === 'pending';
                                                return (
                                                    <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="p-4">
                                                            <div className="font-bold text-gray-800 mb-1">{task.title}</div>
                                                            <div className="text-gray-500 text-xs">{task.description}</div>
                                                        </td>
                                                        <td className="p-4">
                                                            {isPending ? <span className="px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded">Action Required</span> : <span className="px-2.5 py-1 bg-green-100 text-green-800 text-xs font-bold rounded">Completed</span>}
                                                        </td>
                                                        <td className="p-4 text-right">
                                                            {isPending ? (
                                                                <button onClick={() => handleCompleteTask(task.id)} className="px-4 py-1.5 border border-[#0078d4] text-[#0078d4] bg-white font-bold rounded hover:bg-blue-50 transition-colors">Mark Complete</button>
                                                            ) : (
                                                                <span className="text-gray-400 text-xs font-medium">{task.completedAt ? new Date(task.completedAt?.toDate ? task.completedAt.toDate() : task.completedAt).toLocaleDateString() : 'Completed'}</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="bg-white border rounded-sm shadow-sm overflow-hidden">
                                <div className="bg-gray-50 border-b p-4 font-bold text-gray-700 flex items-center gap-2">
                                    <svg className="w-5 h-5 text-[#0078d4]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                    Signatures & Authorizations
                                </div>
                                {signatureRequests.length === 0 ? (
                                    <div className="p-8 text-center text-gray-500 text-sm">
                                        <p>You have no pending signature requests at this time.</p>
                                    </div>
                                ) : (
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-white border-b text-gray-600 text-xs uppercase tracking-wider">
                                            <tr>
                                                <th className="p-4 font-semibold w-1/2">Document Name</th>
                                                <th className="p-4 font-semibold">Status</th>
                                                <th className="p-4 font-semibold text-right">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {signatureRequests.map(req => {
                                                const pdfUrl = getSignatureDocUrl(req);
                                                return (
                                                    <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="p-4 font-medium text-gray-800">{req.documentName || 'Tax Document'}</td>
                                                        <td className="p-4">
                                                            {req.status === 'pending' ? <span className="px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded">Pending Signature</span> : <span className="px-2.5 py-1 bg-green-100 text-green-800 text-xs font-bold rounded">Completed</span>}
                                                        </td>
                                                        <td className="p-4 text-right">
                                                            <div className="inline-flex items-center justify-end gap-2">
                                                                {req.status === 'pending' ? (
                                                                    <button onClick={() => setActiveSignatureModal(req)} className="px-4 py-1.5 bg-[#0078d4] text-white font-bold rounded hover:bg-[#005a9e] transition-colors">Review & Sign</button>
                                                                ) : (
                                                                    <span className="text-gray-400 text-xs font-medium mr-2">
                                                                        {req.timestampUTC ? new Date(req.timestampUTC?.toDate ? req.timestampUTC.toDate() : req.timestampUTC).toLocaleDateString() : 'Signed'}
                                                                    </span>
                                                                )}
                                                                {pdfUrl && (
                                                                    <button
                                                                        onClick={() => handleSaveAsPdf(pdfUrl, req.documentName || 'Document.pdf', req.id)}
                                                                        disabled={savingPdfId === req.id}
                                                                        className="px-3.5 py-1.5 border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 font-bold text-xs rounded transition-colors shadow-sm"
                                                                        title="Save a PDF copy to your computer"
                                                                    >
                                                                        {savingPdfId === req.id ? 'Saving...' : 'Save as PDF'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'UnreadFiles' && (
                        <div className="animate-fade-in space-y-4">
                            <style>{`
                                .quill-wrapper .ql-container { min-height: 120px; font-family: inherit; font-size: 0.875rem; border-bottom-left-radius: 0.5rem; border-bottom-right-radius: 0.5rem; }
                                .quill-wrapper .ql-toolbar { border-top-left-radius: 0.5rem; border-top-right-radius: 0.5rem; background-color: #f9fafb; border-color: #e5e7eb; }
                                .quill-wrapper .ql-container.ql-snow { border-color: #e5e7eb; }
                            `}</style>
                            <h2 className="text-2xl font-semibold text-gray-800">Messages</h2>
                            <div className="bg-white border rounded-lg flex flex-col h-[700px] shadow-sm overflow-hidden">
                                <div className="p-4 border-b bg-gray-50 font-semibold text-sm text-gray-700">
                                    Direct Communication with NVS for {displayClientName}
                                </div>

                                <div className="flex-1 p-6 overflow-y-auto bg-gray-50/30">
                                    {messages.length === 0 ? (
                                        <div className="text-center text-gray-400 mt-10">No messages yet. Send a message to the firm!</div>
                                    ) : (
                                        messages.map((msg, index) => {
                                            const isClient = msg.sender === 'client';

                                            let showDateDivider = false;
                                            let dateLabel = '';

                                            if (msg.timestamp) {
                                                const msgDate = msg.timestamp.toDate ? msg.timestamp.toDate() : new Date(msg.timestamp);
                                                const prevMsg = index > 0 ? messages[index - 1] : null;
                                                let prevDate = null;

                                                if (prevMsg && prevMsg.timestamp) {
                                                    prevDate = prevMsg.timestamp.toDate ? prevMsg.timestamp.toDate() : new Date(prevMsg.timestamp);
                                                }

                                                if (!prevDate || msgDate.toDateString() !== prevDate.toDateString()) {
                                                    showDateDivider = true;
                                                    const today = new Date();
                                                    const yesterday = new Date();
                                                    yesterday.setDate(yesterday.getDate() - 1);

                                                    if (msgDate.toDateString() === today.toDateString()) {
                                                        dateLabel = 'Today';
                                                    } else if (msgDate.toDateString() === yesterday.toDateString()) {
                                                        dateLabel = 'Yesterday';
                                                    } else {
                                                        dateLabel = msgDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                                                    }
                                                }
                                            }

                                            const msgDateObj = msg.timestamp ? (msg.timestamp.toDate ? msg.timestamp.toDate() : new Date(msg.timestamp)) : new Date();
                                            const formattedTime = msgDateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                                            const senderLabel = msg.senderName || (isClient ? 'You' : 'NVS');
                                            const formattedDate = msgDateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) + ' at ' + formattedTime + ' EDT from ' + senderLabel;

                                            return (
                                                <React.Fragment key={msg.id}>
                                                    {showDateDivider && (
                                                        <div className="flex justify-center my-4">
                                                            <span className="bg-gray-200 text-gray-600 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                                                                {dateLabel}
                                                            </span>
                                                        </div>
                                                    )}

                                                    <div className={!isClient ? 'mb-6 p-5 bg-white shadow-sm border border-gray-100 rounded-sm border-l-4 border-[#0078d4]' : 'mb-6 p-5 bg-white shadow-sm border border-gray-100 rounded-sm border-l-4 border-gray-300'}>
                                                        <div className="flex items-center gap-3 mb-4">
                                                            {!isClient ? (
                                                                <div className="bg-[#0078d4] text-white flex items-center justify-center text-[10px] font-bold px-1.5 py-1 rounded-sm tracking-wider">
                                                                    NVS
                                                                </div>
                                                            ) : (
                                                                <div className="w-6 h-6 bg-gray-300 text-gray-700 flex items-center justify-center text-xs font-bold rounded-full">
                                                                    {senderLabel.charAt(0).toUpperCase()}
                                                                </div>
                                                            )}
                                                            <span className="text-sm text-gray-600 font-medium">
                                                                {formattedDate}
                                                            </span>
                                                            {isClient && msg.readByFirm && (
                                                                <span className="text-xs text-[#0078d4] font-semibold ml-auto">Read</span>
                                                            )}
                                                        </div>
                                                        <div
                                                            className="text-sm text-gray-800 space-y-2 [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5 [&_a]:text-[#0078d4] [&_a]:underline"
                                                            dangerouslySetInnerHTML={{ __html: msg.text }}
                                                        />
                                                    </div>
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>

                                <div className="p-6 bg-white border-t border-gray-200">
                                    <div className="quill-wrapper border border-gray-300 rounded-lg bg-white">
                                        <ReactQuill
                                            theme="snow"
                                            value={newMessage}
                                            onChange={setNewMessage}
                                            placeholder={'Type your message regarding ' + displayClientName + ' here...'}
                                            modules={{
                                                toolbar: [
                                                    ['bold', 'italic', 'underline', 'strike'],
                                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                    ['link']
                                                ]
                                            }}
                                        />
                                        <div className="flex justify-end p-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                                            <button
                                                type="button"
                                                onClick={handleSendMessage}
                                                disabled={!newMessage.replace(/(<([^>]+)>)/gi, "").trim()}
                                                className="px-6 py-2 bg-[#0078d4] hover:bg-[#005a9e] disabled:bg-gray-400 text-white font-bold rounded shadow-sm transition-colors text-sm"
                                            >
                                                Send Message
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'BookCall' && (
                        <div className="animate-fade-in space-y-6">
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-800">Request a Call or Appointment</h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    Select a date and available time slot below (minimum {getActivePreparerSchedule().minNoticeHours} hours advance notice required). All requests require confirmation from your preparer.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <form onSubmit={handleRequestAppointment} className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-6 shadow-sm space-y-5">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold uppercase text-gray-600 mb-1.5">
                                                1. Appointment Type
                                            </label>
                                            {(() => {
                                                const allowedTypes = getActivePreparerSchedule().allowedAppointmentTypes;
                                                const activeValue = allowedTypes.includes(bookingType)
                                                    ? bookingType
                                                    : allowedTypes[0] || 'Phone Call';
                                                return (
                                                    <select
                                                        value={activeValue}
                                                        onChange={(e) => setBookingType(e.target.value)}
                                                        className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:border-[#0078d4] outline-none"
                                                    >
                                                        {allowedTypes.includes('Phone Call') && (
                                                            <option value="Phone Call">📞 Phone Call (30 min)</option>
                                                        )}
                                                        {allowedTypes.includes('Video Consultation') && (
                                                            <option value="Video Consultation">💻 Video Consultation (30 min)</option>
                                                        )}
                                                        {allowedTypes.includes('In-Office Meeting') && (
                                                            <option value="In-Office Meeting">🏢 In-Office Meeting (30 min)</option>
                                                        )}
                                                    </select>
                                                );
                                            })()}
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold uppercase text-gray-600 mb-1.5">
                                                2. Select Date
                                            </label>
                                            <input
                                                type="date"
                                                required
                                                min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                                                value={bookingDate}
                                                onChange={(e) => {
                                                    const newDate = e.target.value;
                                                    setBookingDate(newDate);
                                                    const nextSlots = getOpenAvailableSlotsForDate(newDate);
                                                    setBookingTimeSlot(nextSlots.length > 0 ? nextSlots[0].value : '');
                                                }}
                                                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:border-[#0078d4] outline-none"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold uppercase text-gray-600 mb-2">
                                            3. Available Time Slots ({bookingDate})
                                        </label>
                                        {(() => {
                                            const openSlots = getOpenAvailableSlotsForDate(bookingDate);
                                            if (openSlots.length === 0) {
                                                return (
                                                    <div className="p-6 text-center bg-gray-50 border border-dashed border-gray-300 rounded-lg text-xs text-gray-500 font-medium">
                                                        No available time slots on this date (note: bookings require at least {getActivePreparerSchedule().minNoticeHours} hours advance notice). Please select another date.
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                                                    {openSlots.map((slot) => {
                                                        const isSelected = bookingTimeSlot === slot.value;
                                                        return (
                                                            <button
                                                                key={slot.value}
                                                                type="button"
                                                                onClick={() => setBookingTimeSlot(slot.value)}
                                                                className={`py-2.5 px-3 rounded-lg text-xs font-bold border transition-all ${isSelected
                                                                        ? 'bg-[#0078d4] text-white border-[#0078d4] shadow-sm'
                                                                        : 'bg-white text-gray-700 border-gray-300 hover:border-[#0078d4] hover:text-[#0078d4]'
                                                                    }`}
                                                            >
                                                                {slot.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold uppercase text-gray-600 mb-1.5">
                                            4. Topic / Questions for Your Preparer (Optional)
                                        </label>
                                        <textarea
                                            rows="3"
                                            value={bookingTopic}
                                            onChange={(e) => setBookingTopic(e.target.value)}
                                            placeholder="Briefly describe what you would like to discuss (e.g., 2026 tax return review, corporate dividend planning, CRA notice)..."
                                            className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:border-[#0078d4] outline-none"
                                        />
                                    </div>

                                    <div className="flex justify-end pt-2">
                                        <button
                                            type="submit"
                                            disabled={submittingBooking || getOpenAvailableSlotsForDate(bookingDate).length === 0}
                                            className="px-6 py-2.5 bg-[#0078d4] hover:bg-[#005a9e] disabled:bg-gray-400 text-white font-bold rounded-lg shadow-sm transition-colors text-sm"
                                        >
                                            {submittingBooking ? 'Submitting Request...' : 'Request Call / Appointment'}
                                        </button>
                                    </div>
                                </form>

                                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm flex flex-col">
                                    <h3 className="text-base font-bold text-gray-800 border-b pb-3 mb-4">
                                        Your Scheduled & Requested Calls
                                    </h3>
                                    <div className="flex-1 space-y-3 overflow-y-auto max-h-[380px]">
                                        {allCalendarAppointments.filter(a => a.clientId === clientData.id).length === 0 ? (
                                            <p className="text-xs text-gray-400 text-center py-10">
                                                You have no upcoming calls or appointments requested.
                                            </p>
                                        ) : (
                                            allCalendarAppointments
                                                .filter(a => a.clientId === clientData.id)
                                                .sort((a, b) => new Date(b.start || 0) - new Date(a.start || 0))
                                                .map((appt) => {
                                                    const startObj = appt.start ? new Date(appt.start) : null;
                                                    const isReschedule =
                                                        appt.status === 'Reschedule Requested' ||
                                                        appt.status === 'Declined';
                                                    const isConfirmed =
                                                        appt.status === 'Confirmed' ||
                                                        appt.status === 'Scheduled';

                                                    return (
                                                        <div
                                                            key={appt.id}
                                                            className={`p-3.5 rounded-lg border space-y-1.5 ${isReschedule
                                                                    ? 'border-red-200 bg-red-50/40'
                                                                    : isConfirmed
                                                                        ? 'border-green-200 bg-green-50/40'
                                                                        : 'border-amber-200 bg-amber-50/40'
                                                                }`}
                                                        >
                                                            <div className="flex justify-between items-start gap-2">
                                                                <span className="text-xs font-bold text-[#0078d4]">
                                                                    {appt.meetingType || 'Appointment'}
                                                                </span>
                                                                {isReschedule ? (
                                                                    <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-800 border border-red-200">
                                                                        ✕ Reschedule Requested
                                                                    </span>
                                                                ) : isConfirmed ? (
                                                                    <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-green-100 text-green-800 border border-green-200">
                                                                        ✓ Confirmed
                                                                    </span>
                                                                ) : (
                                                                    <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-800 border border-amber-200">
                                                                        ⏳ Requested
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-sm font-bold text-gray-800">
                                                                {startObj ? startObj.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                                                            </p>
                                                            {isReschedule ? (
                                                                <div className="space-y-1">
                                                                    <p className="text-[11px] font-semibold text-red-700">
                                                                        Your preparer is unavailable at this time. Please select a new time slot on the left.
                                                                    </p>
                                                                    {appt.rescheduleNote && (
                                                                        <p className="text-xs italic text-gray-700 bg-white/80 p-2 rounded border border-red-100">
                                                                            "{appt.rescheduleNote}"
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            ) : !isConfirmed ? (
                                                                <p className="text-[11px] font-medium text-amber-700">
                                                                    Pending confirmation from your preparer.
                                                                </p>
                                                            ) : null}
                                                            {appt.topic && (
                                                                <p className="text-xs text-gray-600">{appt.topic}</p>
                                                            )}
                                                            <div className="pt-1 flex justify-end">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleCancelClientAppointment(appt.id, isReschedule)}
                                                                    className="text-[11px] font-semibold text-red-600 hover:underline"
                                                                >
                                                                    {isReschedule ? 'Dismiss & Pick New Time' : 'Cancel Request'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* --- INDIVIDUAL CLIENT SELF-ONBOARDING INTAKE WIZARD MODAL --- */}
            {showOnboardingModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden border border-gray-200">
                        <div className="bg-[#0078d4] text-white px-6 py-5 flex justify-between items-center">
                            <div>
                                <span className="text-[11px] font-bold uppercase tracking-widest text-blue-100 block">
                                    Welcome to Your Secure Client Portal
                                </span>
                                <h2 className="text-xl font-bold mt-0.5">
                                    Complete Your Personal Tax Profile (Step {onboardingStep} of 3)
                                </h2>
                            </div>
                            <div className="flex items-center space-x-2">
                                {[1, 2, 3].map(stepNum => (
                                    <div
                                        key={stepNum}
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ${onboardingStep === stepNum
                                            ? 'bg-white text-[#0078d4] border-white'
                                            : onboardingStep > stepNum
                                                ? 'bg-blue-800 text-white border-blue-300'
                                                : 'bg-transparent text-blue-200 border-blue-300'
                                            }`}
                                    >
                                        {onboardingStep > stepNum ? '✓' : stepNum}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1 space-y-6 text-sm text-gray-800">
                            {onboardingStep === 1 && (
                                <div className="space-y-5">
                                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3.5 text-xs text-blue-900">
                                        Please verify your contact details and complete your personal and residential information below so we can prepare your tax filings accurately.
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">First Name *</label>
                                            <input
                                                type="text"
                                                value={intakeForm.firstName}
                                                onChange={(e) => setIntakeForm(prev => ({ ...prev, firstName: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:border-[#0078d4]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Last Name *</label>
                                            <input
                                                type="text"
                                                value={intakeForm.lastName}
                                                onChange={(e) => setIntakeForm(prev => ({ ...prev, lastName: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:border-[#0078d4]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Date of Birth *</label>
                                            <input
                                                type="date"
                                                value={intakeForm.dob}
                                                onChange={(e) => setIntakeForm(prev => ({ ...prev, dob: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:border-[#0078d4]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Social Insurance Number (SIN) *</label>
                                            <input
                                                type="text"
                                                placeholder="XXX-XXX-XXX"
                                                value={intakeForm.sin}
                                                onChange={(e) => setIntakeForm(prev => ({ ...prev, sin: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:border-[#0078d4]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Email Address *</label>
                                            <input
                                                type="email"
                                                value={intakeForm.email}
                                                readOnly
                                                className="w-full border border-gray-200 bg-gray-100 text-gray-500 rounded-lg p-2.5 cursor-not-allowed"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Primary Phone *</label>
                                            <input
                                                type="tel"
                                                value={intakeForm.phone1}
                                                onChange={(e) => setIntakeForm(prev => ({ ...prev, phone1: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:border-[#0078d4]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Secondary Phone (Optional)</label>
                                            <input
                                                type="tel"
                                                value={intakeForm.phone2}
                                                onChange={(e) => setIntakeForm(prev => ({ ...prev, phone2: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:border-[#0078d4]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">US SSN (If applicable)</label>
                                            <input
                                                type="text"
                                                value={intakeForm.ssn}
                                                onChange={(e) => setIntakeForm(prev => ({ ...prev, ssn: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:border-[#0078d4]"
                                            />
                                        </div>
                                    </div>

                                    <div className="border-t border-gray-200 pt-4">
                                        <h3 className="text-sm font-bold text-gray-900 mb-3">Residential Address</h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div className="sm:col-span-2">
                                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Street Address *</label>
                                                <input
                                                    type="text"
                                                    placeholder="123 Main Street, Unit 4"
                                                    value={intakeForm.street}
                                                    onChange={(e) => setIntakeForm(prev => ({ ...prev, street: e.target.value }))}
                                                    className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:border-[#0078d4]"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">City *</label>
                                                <input
                                                    type="text"
                                                    value={intakeForm.city}
                                                    onChange={(e) => setIntakeForm(prev => ({ ...prev, city: e.target.value }))}
                                                    className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:border-[#0078d4]"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Province *</label>
                                                <select
                                                    value={intakeForm.province}
                                                    onChange={(e) => setIntakeForm(prev => ({ ...prev, province: e.target.value }))}
                                                    className="w-full border border-gray-300 rounded-lg p-2.5 bg-white focus:outline-none focus:border-[#0078d4]"
                                                >
                                                    {PROVINCES.map(prov => (
                                                        <option key={prov} value={prov}>{prov}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Postal Code *</label>
                                                <input
                                                    type="text"
                                                    placeholder="M5V 2T6"
                                                    value={intakeForm.postal}
                                                    onChange={(e) => setIntakeForm(prev => ({ ...prev, postal: e.target.value }))}
                                                    className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:border-[#0078d4]"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Country</label>
                                                <input
                                                    type="text"
                                                    value={intakeForm.country}
                                                    onChange={(e) => setIntakeForm(prev => ({ ...prev, country: e.target.value }))}
                                                    className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:border-[#0078d4]"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {onboardingStep === 2 && (
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Marital Status *</label>
                                        <select
                                            value={intakeForm.maritalStatus}
                                            onChange={(e) => setIntakeForm(prev => ({ ...prev, maritalStatus: e.target.value }))}
                                            className="w-full border border-gray-300 rounded-lg p-2.5 bg-white focus:outline-none focus:border-[#0078d4]"
                                        >
                                            {MARITAL_STATUSES.map(st => (
                                                <option key={st} value={st}>{st}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {(intakeForm.maritalStatus === 'Married' || intakeForm.maritalStatus === 'Common-Law') && (
                                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-4">
                                            <h3 className="text-sm font-bold text-gray-900 border-b pb-2">
                                                Spouse / Common-Law Partner Information
                                            </h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Spouse First Name *</label>
                                                    <input
                                                        type="text"
                                                        value={intakeForm.spouse.firstName}
                                                        onChange={(e) => setIntakeForm(prev => ({
                                                            ...prev,
                                                            spouse: { ...prev.spouse, firstName: e.target.value }
                                                        }))}
                                                        className="w-full border border-gray-300 rounded-lg p-2 bg-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Spouse Last Name *</label>
                                                    <input
                                                        type="text"
                                                        value={intakeForm.spouse.lastName}
                                                        onChange={(e) => setIntakeForm(prev => ({
                                                            ...prev,
                                                            spouse: { ...prev.spouse, lastName: e.target.value }
                                                        }))}
                                                        className="w-full border border-gray-300 rounded-lg p-2 bg-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Spouse Date of Birth</label>
                                                    <input
                                                        type="date"
                                                        value={intakeForm.spouse.dob}
                                                        onChange={(e) => setIntakeForm(prev => ({
                                                            ...prev,
                                                            spouse: { ...prev.spouse, dob: e.target.value }
                                                        }))}
                                                        className="w-full border border-gray-300 rounded-lg p-2 bg-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Spouse SIN</label>
                                                    <input
                                                        type="text"
                                                        placeholder="XXX-XXX-XXX"
                                                        value={intakeForm.spouse.sin}
                                                        onChange={(e) => setIntakeForm(prev => ({
                                                            ...prev,
                                                            spouse: { ...prev.spouse, sin: e.target.value }
                                                        }))}
                                                        className="w-full border border-gray-300 rounded-lg p-2 bg-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Spouse Email</label>
                                                    <input
                                                        type="email"
                                                        value={intakeForm.spouse.email}
                                                        onChange={(e) => setIntakeForm(prev => ({
                                                            ...prev,
                                                            spouse: { ...prev.spouse, email: e.target.value }
                                                        }))}
                                                        className="w-full border border-gray-300 rounded-lg p-2 bg-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Spouse Phone</label>
                                                    <input
                                                        type="tel"
                                                        value={intakeForm.spouse.phone1}
                                                        onChange={(e) => setIntakeForm(prev => ({
                                                            ...prev,
                                                            spouse: { ...prev.spouse, phone1: e.target.value }
                                                        }))}
                                                        className="w-full border border-gray-300 rounded-lg p-2 bg-white"
                                                    />
                                                </div>
                                            </div>

                                            <label className="flex items-center space-x-2 text-xs font-semibold text-gray-700 pt-2">
                                                <input
                                                    type="checkbox"
                                                    checked={sameAddressForSpouse}
                                                    onChange={(e) => setSameAddressForSpouse(e.target.checked)}
                                                    className="rounded border-gray-300"
                                                />
                                                <span>Spouse resides at the same residential address</span>
                                            </label>
                                        </div>
                                    )}

                                    <div className="border-t border-gray-200 pt-4">
                                        <div className="flex justify-between items-center mb-3">
                                            <h3 className="text-sm font-bold text-gray-900">Dependents (Children / Family Members)</h3>
                                            <button
                                                type="button"
                                                onClick={handleAddDependent}
                                                className="px-3 py-1.5 bg-blue-50 text-[#0078d4] hover:bg-blue-100 rounded-lg text-xs font-bold transition-colors"
                                            >
                                                + Add Dependent
                                            </button>
                                        </div>

                                        {intakeForm.dependents.length === 0 ? (
                                            <p className="text-xs text-gray-400 italic">No dependents added.</p>
                                        ) : (
                                            <div className="space-y-3">
                                                {intakeForm.dependents.map((dep, idx) => (
                                                    <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                                                        <div>
                                                            <label className="block text-[10px] font-bold uppercase text-gray-600 mb-1">First Name</label>
                                                            <input
                                                                type="text"
                                                                value={dep.firstName}
                                                                onChange={(e) => handleDependentChange(idx, 'firstName', e.target.value)}
                                                                className="w-full border border-gray-300 rounded p-1.5 text-xs bg-white"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-[10px] font-bold uppercase text-gray-600 mb-1">Last Name</label>
                                                            <input
                                                                type="text"
                                                                value={dep.lastName}
                                                                onChange={(e) => handleDependentChange(idx, 'lastName', e.target.value)}
                                                                className="w-full border border-gray-300 rounded p-1.5 text-xs bg-white"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-[10px] font-bold uppercase text-gray-600 mb-1">Date of Birth</label>
                                                            <input
                                                                type="date"
                                                                value={dep.dob}
                                                                onChange={(e) => handleDependentChange(idx, 'dob', e.target.value)}
                                                                className="w-full border border-gray-300 rounded p-1.5 text-xs bg-white"
                                                            />
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex-1">
                                                                <label className="block text-[10px] font-bold uppercase text-gray-600 mb-1">SIN (Optional)</label>
                                                                <input
                                                                    type="text"
                                                                    value={dep.sin}
                                                                    onChange={(e) => handleDependentChange(idx, 'sin', e.target.value)}
                                                                    className="w-full border border-gray-300 rounded p-1.5 text-xs bg-white"
                                                                />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveDependent(idx)}
                                                                className="text-red-500 hover:text-red-700 font-bold text-xs px-2 py-1.5"
                                                                title="Remove Dependent"
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {onboardingStep === 3 && (
                                <div className="space-y-6">
                                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3.5 text-xs text-blue-900">
                                        Almost done! Please answer these brief questions regarding your tax profile.
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                                Are you Self-Employed / Sole Proprietor?
                                            </label>
                                            <select
                                                value={intakeForm.isSelfEmployed}
                                                onChange={(e) => setIntakeForm(prev => ({ ...prev, isSelfEmployed: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-lg p-2.5 bg-white"
                                            >
                                                <option value="No">No</option>
                                                <option value="Yes">Yes</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                                Do you own Rental Property?
                                            </label>
                                            <select
                                                value={intakeForm.hasRentalProperty}
                                                onChange={(e) => setIntakeForm(prev => ({ ...prev, hasRentalProperty: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-lg p-2.5 bg-white"
                                            >
                                                <option value="No">No</option>
                                                <option value="Yes">Yes</option>
                                            </select>
                                        </div>

                                        {intakeForm.isSelfEmployed === 'Yes' && (
                                            <>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                                        GST / HST Number (If registered)
                                                    </label>
                                                    <input
                                                        type="text"
                                                        placeholder="123456789RT0001"
                                                        value={intakeForm.gstNumber}
                                                        onChange={(e) => setIntakeForm(prev => ({ ...prev, gstNumber: e.target.value }))}
                                                        className="w-full border border-gray-300 rounded-lg p-2.5"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                                        Using GST Quick Method?
                                                    </label>
                                                    <select
                                                        value={intakeForm.gstQuickMethod}
                                                        onChange={(e) => setIntakeForm(prev => ({ ...prev, gstQuickMethod: e.target.value }))}
                                                        className="w-full border border-gray-300 rounded-lg p-2.5 bg-white"
                                                    >
                                                        <option value="No">No</option>
                                                        <option value="Yes">Yes</option>
                                                    </select>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <div className="border-t border-gray-200 pt-4">
                                        <h3 className="text-xs font-bold text-gray-500 uppercase mb-3">
                                            Newcomer / Emigrant Dates (Leave blank if you lived in Canada all year)
                                        </h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Arrival Date in Canada</label>
                                                <input
                                                    type="date"
                                                    value={intakeForm.arrivalDate}
                                                    onChange={(e) => setIntakeForm(prev => ({ ...prev, arrivalDate: e.target.value }))}
                                                    className="w-full border border-gray-300 rounded-lg p-2.5"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Departure Date from Canada</label>
                                                <input
                                                    type="date"
                                                    value={intakeForm.departureDate}
                                                    onChange={(e) => setIntakeForm(prev => ({ ...prev, departureDate: e.target.value }))}
                                                    className="w-full border border-gray-300 rounded-lg p-2.5"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                            {onboardingStep > 1 ? (
                                <button
                                    type="button"
                                    onClick={() => setOnboardingStep(prev => prev - 1)}
                                    disabled={submittingOnboarding}
                                    className="px-5 py-2.5 bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 font-bold text-xs rounded-lg transition-colors"
                                >
                                    &larr; Back
                                </button>
                            ) : (
                                <div />
                            )}

                            {onboardingStep < 3 ? (
                                <button
                                    type="button"
                                    onClick={handleIntakeStepNext}
                                    className="px-6 py-2.5 bg-[#0078d4] hover:bg-[#005a9e] text-white font-bold text-xs rounded-lg shadow transition-colors"
                                >
                                    Next Step &rarr;
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleCompleteOnboarding}
                                    disabled={submittingOnboarding}
                                    className="px-8 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-lg shadow transition-colors disabled:opacity-50"
                                >
                                    {submittingOnboarding ? 'Saving Profile...' : '✓ Complete Onboarding & Enter Portal'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showFirmInfo && (
                <>
                    <div className="fixed inset-0 bg-black bg-opacity-20 z-40" onClick={() => setShowFirmInfo(false)}></div>
                    <div className="fixed top-0 right-0 h-full w-[350px] bg-white shadow-2xl z-50 animate-slide-in-right flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="font-bold text-gray-800">Firm Information</h3>
                            <button onClick={() => setShowFirmInfo(false)} className="text-gray-500 hover:text-gray-800"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                        </div>
                        <div className="p-6">
                            <div className="flex items-center gap-6 mb-8 border-b pb-6">
                                <div className="text-5xl font-black tracking-tighter">{firmTemplate?.companyName || 'NVS'}</div>
                            </div>
                            <div className="space-y-0">
                                <div className="flex items-start gap-4 py-6 border-b">
                                    <svg className="w-8 h-8 text-[#0078d4]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    <div className="text-gray-700 text-sm">{firmTemplate?.address || 'M145 - 1460 The Queensway, Canada'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* --- INVOICE / RECEIPT LIVE VIEWER MODAL --- */}
            {viewingInvoice && activeBranding && (() => {
                const invTotal = parseFloat(viewingInvoice.total || 0);
                const invTax = parseFloat(viewingInvoice.taxAmount ?? viewingInvoice.tax ?? 0);
                const invSubtotal = viewingInvoice.subtotal !== undefined
                    ? parseFloat(viewingInvoice.subtotal)
                    : (invTotal - invTax);
                const invBalance = viewingInvoice.balance !== undefined
                    ? parseFloat(viewingInvoice.balance)
                    : (viewingInvoice.status === 'Paid' ? 0 : invTotal);
                const hasPaymentsList = Array.isArray(viewingInvoice.payments) && viewingInvoice.payments.length > 0;
                const invPaid = hasPaymentsList
                    ? viewingInvoice.payments.reduce((sum, p) => sum + (parseFloat(p.amountReceived ?? p.amount) || 0), 0)
                    : Math.max(0, invTotal - invBalance);

                const isPaidStatus = viewingInvoice.status === 'Paid' || (invTotal > 0 && invBalance <= 0.005);
                const isPartialStatus = !isPaidStatus && (viewingInvoice.status === 'Partially Paid' || invPaid > 0);
                const docHeaderTitle = isPaidStatus ? 'Receipt' : (isPartialStatus ? 'Partial Receipt' : 'Invoice');
                const defaultInvYear = String(viewingInvoice.year || new Date(viewingInvoice.invoiceDate || viewingInvoice.id || Date.now()).getFullYear());

                const billedToName = viewingInvoice.clientSnapshot?.name || displayClientName;
                const billedToEmail = viewingInvoice.clientSnapshot?.email || clientData.email || '';
                const billedToAddress = viewingInvoice.clientSnapshot?.addressFormatted || '';

                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                        <div className="bg-white w-full max-w-2xl rounded-lg shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">

                            <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                                <h3 className="font-bold text-lg text-gray-800">
                                    {viewingInvoice.type === 'Retainer' ? 'Retainer Receipt' : docHeaderTitle} #{viewingInvoice.invoiceNumber || viewingInvoice.id}
                                </h3>
                                <button onClick={() => setViewingInvoice(null)} className="text-gray-500 hover:text-gray-800">
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>

                            <div className="p-8 overflow-y-auto flex-1 text-sm text-gray-700 space-y-6 bg-white">
                                <div className="flex justify-between items-start">
                                    <div className="flex flex-col items-start">
                                        {activeBranding.showLogo && activeBranding.logoBase64 && (
                                            <img src={activeBranding.logoBase64} alt="Logo" className="max-h-16 max-w-[160px] object-contain mb-2" />
                                        )}
                                        {activeBranding.showFirmName && (
                                            <div className="text-2xl font-black tracking-tight mb-1" style={{ color: activeBranding.headerColor }}>
                                                {activeBranding.companyName}
                                            </div>
                                        )}
                                        {activeBranding.showFirmAddress && activeBranding.address && (
                                            <p className="text-gray-500 leading-relaxed">{activeBranding.address}</p>
                                        )}
                                        {activeBranding.showFirmContact && (activeBranding.phone || activeBranding.email) && (
                                            <p className="text-gray-500 leading-relaxed">
                                                {[activeBranding.phone, activeBranding.email].filter(Boolean).join(' | ')}
                                            </p>
                                        )}
                                        {activeBranding.showFirmWebsite && activeBranding.website && (
                                            <p className="text-gray-500 leading-relaxed">{activeBranding.website}</p>
                                        )}
                                        {activeBranding.showTaxNumber && activeBranding.taxNumber && (
                                            <p className="text-gray-500 text-xs mt-1">Tax #: {activeBranding.taxNumber}</p>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <p className="text-2xl font-bold uppercase tracking-widest mb-3" style={{ color: activeBranding.headerColor }}>
                                            {docHeaderTitle}
                                        </p>
                                        <p className="mb-1"><span className="font-semibold text-gray-800">Date:</span> {new Date(viewingInvoice.invoiceDate || viewingInvoice.id).toLocaleDateString()}</p>
                                        <p className="mb-1"><span className="font-semibold text-gray-800">Status:</span> <span className={isPaidStatus ? 'text-green-600 font-bold' : 'text-amber-600 font-bold'}>{viewingInvoice.status || 'Pending'}</span></p>
                                        {activeBranding.showPreparer && (activeBranding.preparerDisplayName || viewingInvoice.user) && (
                                            <p className="text-xs text-gray-500 mt-1">Prepared By: {activeBranding.preparerDisplayName || viewingInvoice.user}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-gray-200">
                                    <p className="font-semibold text-gray-800 mb-1">Billed To:</p>
                                    <p className="text-gray-700 font-medium">{billedToName}</p>
                                    {billedToAddress && <p className="text-gray-500 text-xs">{billedToAddress}</p>}
                                    {billedToEmail && <p className="text-gray-500">{billedToEmail}</p>}
                                </div>

                                <table className="w-full mt-4 border-collapse">
                                    <thead className="bg-gray-50 border-y border-gray-200">
                                        <tr>
                                            <th className="py-2 px-3 text-left font-semibold text-gray-800 w-1/4">Year</th>
                                            <th className="py-2 px-3 text-left font-semibold text-gray-800 w-1/2">Service Details</th>
                                            <th className="py-2 px-3 text-right font-semibold text-gray-800 w-1/4">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {viewingInvoice.items && viewingInvoice.items.length > 0 ? (
                                            viewingInvoice.items.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td className="py-2 px-3 font-medium text-gray-800">{item.year || defaultInvYear}</td>
                                                    <td className="py-2 px-3 text-gray-600">{item.description || 'Professional Services'}</td>
                                                    <td className="py-2 px-3 text-right text-gray-800 font-medium">${parseFloat(item.amount ?? item.price ?? 0).toFixed(2)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td className="py-2 px-3 font-medium text-gray-800">{defaultInvYear}</td>
                                                <td className="py-2 px-3 text-gray-600">Professional Services</td>
                                                <td className="py-2 px-3 text-right text-gray-800 font-medium">${invSubtotal.toFixed(2)}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                                <div className="flex flex-col sm:flex-row justify-between items-start pt-4 gap-6">
                                    <div className="w-full sm:w-1/2 space-y-4">
                                        {hasPaymentsList && (
                                            <div className="border border-gray-200 rounded-md overflow-hidden bg-gray-50">
                                                <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 text-xs font-bold uppercase text-gray-600 tracking-wider">
                                                    Payment Record
                                                </div>
                                                <table className="w-full text-xs bg-white">
                                                    <thead className="border-b border-gray-100 text-gray-500">
                                                        <tr>
                                                            <th className="p-2 text-left">Method</th>
                                                            <th className="p-2 text-left">Date Taken</th>
                                                            {activeBranding.showPaymentTakenBy && <th className="p-2 text-left">Taken By</th>}
                                                            <th className="p-2 text-right">Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {viewingInvoice.payments.map((p, idx) => (
                                                            <tr key={idx}>
                                                                <td className="p-2 font-semibold text-gray-800">{p.paymentMethod || p.method || 'Payment'}</td>
                                                                <td className="p-2 text-gray-600">{new Date(p.paymentDate || p.date || p.paymentId || viewingInvoice.invoiceDate).toLocaleDateString()}</td>
                                                                {activeBranding.showPaymentTakenBy && <td className="p-2 text-gray-600">{p.paidTo || p.receivedBy || p.user || '-'}</td>}
                                                                <td className="p-2 text-right font-semibold text-green-600">${parseFloat(p.amountReceived ?? p.amount ?? 0).toFixed(2)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                        {activeBranding.showBankDetails && activeBranding.bankDetails && (
                                            <div>
                                                <p className="text-xs font-bold uppercase text-gray-400 tracking-wider mb-1">Payment Instructions</p>
                                                <p className="text-xs text-gray-600 whitespace-pre-line">{activeBranding.bankDetails}</p>
                                            </div>
                                        )}
                                        {activeBranding.showTerms && activeBranding.termsAndConditions && (
                                            <div>
                                                <p className="text-xs font-bold uppercase text-gray-400 tracking-wider mb-1">Terms & Conditions</p>
                                                <p className="text-xs text-gray-500 whitespace-pre-line">{activeBranding.termsAndConditions}</p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="w-full sm:w-64 space-y-2">
                                        <div className="flex justify-between text-gray-600">
                                            <span>Subtotal:</span>
                                            <span>${invSubtotal.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-gray-600">
                                            <span>Tax (GST/HST):</span>
                                            <span>${invTax.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between font-bold text-base text-gray-900 border-t border-gray-200 pt-2">
                                            <span>Total:</span>
                                            <span>${invTotal.toFixed(2)}</span>
                                        </div>
                                        {hasPaymentsList ? (
                                            viewingInvoice.payments.map((p, idx) => (
                                                <div key={idx} className="flex justify-between text-green-600 text-xs font-medium">
                                                    <span>
                                                        Paid ({p.paymentMethod || 'Payment'} - {new Date(p.paymentDate || p.paymentId || viewingInvoice.invoiceDate).toLocaleDateString()}):
                                                    </span>
                                                    <span>-${parseFloat(p.amountReceived ?? p.amount ?? 0).toFixed(2)}</span>
                                                </div>
                                            ))
                                        ) : (
                                            invPaid > 0 && (
                                                <div className="flex justify-between text-green-600 font-medium">
                                                    <span>Amount Paid:</span>
                                                    <span>-${invPaid.toFixed(2)}</span>
                                                </div>
                                            )
                                        )}
                                        <div className="flex justify-between font-extrabold text-lg border-t-2 pt-2" style={{ color: activeBranding.headerColor, borderColor: activeBranding.headerColor }}>
                                            <span>Balance Due:</span>
                                            <span>${invBalance.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>

                                {activeBranding.footerText && (
                                    <div className="pt-6 border-t border-gray-100 text-center text-xs text-gray-400 italic">
                                        {activeBranding.footerText}
                                    </div>
                                )}
                            </div>

                            <div className="p-4 border-t bg-gray-50 rounded-b-lg flex justify-end gap-3">
                                <button onClick={() => handleSaveInvoiceAsPdf(viewingInvoice)} className="px-5 py-2 bg-[#0078d4] hover:bg-[#005a9e] text-white rounded font-medium transition-colors shadow-sm">
                                    Save as PDF
                                </button>
                                <button onClick={() => setViewingInvoice(null)} className="px-6 py-2 bg-gray-900 text-white rounded hover:bg-black font-medium transition-colors">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {activeSignatureModal && (
                <SignatureModal
                    clientId={clientData.id}
                    requestId={activeSignatureModal.id}
                    documentName={activeSignatureModal.documentName || activeSignatureModal.name || 'Document'}
                    documentUrl={getSignatureDocUrl(activeSignatureModal)}
                    clientData={clientData}
                    onSuccess={async () => {
                        const signedDocName = activeSignatureModal.documentName || activeSignatureModal.name || 'Document';
                        setActiveSignatureModal(null);
                        try {
                            await updateDoc(doc(db, 'clients', clientData.id), {
                                history: arrayUnion({
                                    user: 'Client Portal',
                                    timestamp: new Date().toLocaleString(),
                                    action: `Client signed document '${signedDocName}'.`
                                })
                            });
                            await addDoc(collection(db, 'firmNotifications'), {
                                type: 'signature',
                                title: 'Document Signed by Client',
                                message: `${displayClientName} signed and submitted "${signedDocName}".`,
                                clientId: clientData.id,
                                clientName: displayClientName,
                                isRead: false,
                                createdAt: serverTimestamp()
                            });
                        } catch (e) { }
                    }}
                    onCancel={() => setActiveSignatureModal(null)}
                />
            )}
        </div>
    );
};

export default ClientDashboard;